// TPTalk Rocket.Chat REST 클라이언트 (vite proxy 사용 → /api → localhost:3000)
// PIPA: 메시지 본문은 절대 로그에 남기지 않는다.
import { diagLog } from '../desktop/diag';
import { isTauri } from '../desktop/env';
import { useAuthStore } from '../stores/authStore';
import { ensureExt, extOf, resolveDownloadName } from './fileName';
import { stabilizeUploadFile, UploadReadError } from './uploadPrep';
import { reviveEjsonDates } from './messageMerge';
import { getDownloadReveal, getDownloadDir, getDownloadAsk, getNameLang2 } from './prefs';
import i18next from 'i18next';

export interface RcLoginResult {
	authToken: string;
	userId: string;
	me: {
		_id: string;
		username: string;
		name?: string;
		emails?: { address: string }[];
		avatarUrl?: string;
		roles?: string[];
		customFields?: { membership?: string; nameConfirmed?: boolean; signupAt?: string };
		// 🔐 임시 비밀번호 발급(비번 찾기) 시 사이드카가 세팅 — LoginPage 가 새 비번 설정을 강제 (2026-08-24)
		requirePasswordChange?: boolean;
	};
}

export interface RcChannel {
	_id: string;
	name?: string;
	fname?: string;
	t: 'c' | 'p' | 'd' | 'l';
	usersCount?: number;
	unread?: number;
	ls?: string;              // 내 마지막 읽은 시각 (RC subscription.ls — ...s 스프레드로 실려 옴). 안읽은 위치 진입 앵커용 (2026-08-22)
	userMentions?: number;   // 나를 @멘션한 안읽음 수 (RC subscription.userMentions — ...s 스프레드로 실려 옴)
	tunread?: string[];      // 🧵 새 댓글이 있는 스레드 원문 id 목록 (subscription.tunread — 참여자에게만, 방 unread 와 별개 축. api/thread.ts)
	f?: boolean;              // ⭐ 즐겨찾기 (subscription.f) — 사이드바 최상단 정렬 + 별 아이콘
	muted?: boolean;          // 방별 알림 음소거 (subscription.disableNotifications)
	notifPref?: 'all' | 'mentions' | 'nothing'; // 방별 알림 3단계 (subscription.desktopNotifications, 2026-07-28)
	lastMessage?: {
		msg: string;
		ts: string;
		u: { _id?: string; username: string; name?: string };
		file?: { name?: string; type?: string };
		attachments?: Array<{ title?: string; image_url?: string; type?: string }>;
		t?: string; // 시스템 메시지 타입 (uj/ul/au/ru/room_changed_topic 등)
	};
	updatedAt?: string;
	announcement?: string;
	ro?: boolean;            // 읽기 전용(공지방 등)
	description?: string;    // 🏷 토픽 비고 (토픽 설정 모달, 토픽 관리자만) — 헤더 2번째 줄
	topic?: string;          // 🏷 토픽 PJT 제목 (2026-09-13 사용자 "토픽 속성(PJT 제목과 비고) 등록·표기") — RC room.topic, rooms.saveRoomSettings roomTopic
}

// 채널(토픽) 이름 변경 — /api/tpt/room/rename 경유.
// 한글/특수문자 이름은 RC 기본 검증에서 거부되므로 서버가 UI_Allow_room_names_with_special_chars 를 보장 후 rename.
export async function rcRenameRoom(token: string, userId: string, room: RcChannel, newName: string): Promise<boolean> {
	const res = await fetch('/api/tpt/room/rename', {
		method: 'POST', headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ roomId: room._id, name: newName, t: room.t, token, userId }),
	});
	if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || `rename ${res.status}`); }
	return true;
}

// 1:1 대화 → 토픽 전환 — /api/tpt/room/to-topic 경유 (2026-08-17).
// RC 는 DM 타입 변경을 금지하므로(saveRoomType "Can't change type of direct rooms") REST 경로가 없다.
// 사이드카가 mongo in-place 로 t='p' + 토픽 slug + 표시명(fname)을 세팅 → 대화 이력은 그대로 유지된다.
export async function rcConvertDmToTopic(token: string, userId: string, roomId: string, name: string): Promise<{ name: string; fname: string }> {
	const res = await fetch('/api/tpt/room/to-topic', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token, 'X-User-Id': userId },
		body: JSON.stringify({ roomId, name }),
	});
	const d = await res.json().catch(() => ({}));
	if (!res.ok || !d.ok) throw new Error(d.error || `to-topic ${res.status}`);
	return { name: String(d.name || ''), fname: String(d.fname || name) };
}

// 🔎 참여자 이름으로 내 방 찾기 — /api/tpt/room/search-members (2026-08-20).
//   사이드바 검색이 방 이름·마지막 메시지만 보던 것을 보완. 실패해도 검색 자체는 계속되게 빈 배열 반환.
//   인증 헤더는 installTptAuthFetch 가 자동으로 붙인다(GUIDE §2).
export async function rcSearchRoomsByMember(q: string, signal?: AbortSignal): Promise<string[]> {
	const term = q.trim();
	if (!term) return [];
	try {
		const res = await fetch(`/api/tpt/room/search-members?q=${encodeURIComponent(term)}`, { signal });
		if (!res.ok) return [];
		const d = await res.json().catch(() => ({}));
		return Array.isArray(d.rids) ? (d.rids as unknown[]).map((r) => String(r)) : [];
	} catch { return []; }   // AbortError(입력 중 취소) 포함 — 조용히 무시
}

// 공지 설정 — /api/tpt/room/announcement 경유 (2026-09-16 사용자 "토픽에서 공지등록은 토픽관리자가 아니더라도 되도록").
//   🔴 종전 RC REST(channels/groups.setAnnouncement)는 saveRoomSettings 가 'edit-room'(admin/owner/moderator)을
//      강제해 일반 멤버는 400 으로 거부됐다. 사이드카가 **그 방 멤버인지**만 확인하고 mongo 를 직접 쓴다
//      (방 이름 변경·설명 변경과 같은 수법). 비멤버는 서버가 403.
export async function rcSetAnnouncement(token: string, userId: string, room: RcChannel, announcement: string): Promise<boolean> {
	const res = await fetch('/api/tpt/room/announcement', {
		method: 'POST', headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ roomId: room._id, announcement, token, userId }),
	});
	if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || `announce ${res.status}`); }
	return true;
}

// 토픽 상세 설정 — rooms.saveRoomSettings (이름/설명/읽기전용 등 한 번에).
export async function rcSaveRoomSettings(token: string, userId: string, rid: string, settings: Record<string, unknown>): Promise<boolean> {
	const res = await fetch(`${API_BASE}/rooms.saveRoomSettings`, {
		method: 'POST', headers: authHeaders(token, userId),
		body: JSON.stringify({ rid, ...settings }),
	});
	const d = await res.json().catch(() => ({}));
	if (!res.ok || d.success === false) throw new Error(d.error || d.errorType || `saveRoomSettings ${res.status}`);
	return true;
}

// 토픽 삭제 / 나가기.
export async function rcDeleteRoom(token: string, userId: string, room: RcChannel): Promise<boolean> {
	const ep = room.t === 'p' ? 'groups.delete' : 'channels.delete';
	const res = await fetch(`${API_BASE}/${ep}`, { method: 'POST', headers: authHeaders(token, userId), body: JSON.stringify({ roomId: room._id }) });
	const d = await res.json().catch(() => ({}));
	if (!res.ok || d.success === false) throw new Error(d.error || d.errorType || `delete ${res.status}`);
	return true;
}

// 멤버 권한 — owner/moderator 임명, 강퇴.
export async function rcSetRoomRole(token: string, userId: string, room: RcChannel, targetUserId: string, role: 'owner' | 'moderator', add: boolean): Promise<boolean> {
	const base = room.t === 'p' ? 'groups' : 'channels';
	const verb = role === 'owner' ? (add ? 'addOwner' : 'removeOwner') : (add ? 'addModerator' : 'removeModerator');
	const res = await fetch(`${API_BASE}/${base}.${verb}`, { method: 'POST', headers: authHeaders(token, userId), body: JSON.stringify({ roomId: room._id, userId: targetUserId }) });
	const d = await res.json().catch(() => ({}));
	// errorType 보존 — 호출부가 error-remove-last-owner 등을 사용자 문구로 바꿀 수 있게 (2026-08-12)
	if (!res.ok || d.success === false) throw new RcActionError(res.status, d.errorType || '', d.error || d.errorType || `${verb} ${res.status}`);
	return true;
}
export async function rcKickUser(token: string, userId: string, room: RcChannel, targetUserId: string): Promise<boolean> {
	const ep = room.t === 'p' ? 'groups.kick' : 'channels.kick';
	const res = await fetch(`${API_BASE}/${ep}`, { method: 'POST', headers: authHeaders(token, userId), body: JSON.stringify({ roomId: room._id, userId: targetUserId }) });
	const d = await res.json().catch(() => ({}));
	if (!res.ok || d.success === false) throw new Error(d.error || d.errorType || `kick ${res.status}`);
	notifyMembersChanged(room._id); // 캐시 무효화 + 목록 그리는 화면 전부 갱신 (2026-08-19)
	return true;
}

// 🚪 멤버 내보내기(관리형, 2026-07-15) — 사이드카가 admin 으로 처리: ① 마지막 소유자(퇴사한 토픽 생성자 등)면
//   소유권 승계 후 강퇴 ② 대상이 퇴사/삭제(active=false)면 토픽관리자가 아니어도 누구나 내보내기 가능.
//   권한 부족/실패는 서버가 판정해 에러 반환.
export async function rcRoomKickManaged(roomId: string, targetUserId: string): Promise<{ ok: boolean; retired?: boolean }> {
	const { authToken, userId } = useAuthStore.getState();
	if (!authToken || !userId) throw new Error('auth required');
	const res = await fetch(`${RC_ORIGIN}/api/tpt/room/kick`, {
		method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders(authToken, userId) },
		body: JSON.stringify({ roomId, targetUserId }),
	});
	const d = await res.json().catch(() => ({}));
	if (!res.ok || d.ok === false) throw new Error(d.error || `kick ${res.status}`);
	notifyMembersChanged(roomId);
	return { ok: true, retired: !!d.retired };
}

// 방 멤버 권한 — channels.roles/groups.roles → username별 roles 배열.
export async function rcRoomRoles(token: string, userId: string, room: RcChannel): Promise<Record<string, string[]>> {
	const ep = room.t === 'p' ? 'groups.roles' : 'channels.roles';
	const res = await fetch(`${API_BASE}/${ep}?roomId=${encodeURIComponent(room._id)}`, { headers: authHeaders(token, userId) });
	if (!res.ok) return {};
	const d = await res.json().catch(() => ({}));
	const map: Record<string, string[]> = {};
	for (const r of (d.roles || []) as Array<{ u?: { username?: string }; roles?: string[] }>) {
		if (r.u?.username) map[r.u.username] = r.roles || [];
	}
	return map;
}

// 토픽(채널/그룹) 나가기 — RC 는 마지막 방장(owner)의 leave 를 error-you-are-last-owner 로
// 거부한다 (DM 은 owner 개념 없음). 그 경우 승계 후보를 찾아 호출측이 UX 분기하게 한다 (2026-06-11).
export type RcLeaveOutcome =
	| { status: 'left' }
	| { status: 'last-owner'; successor: { _id: string; username: string; name?: string } | null };

async function postLeave(token: string, userId: string, room: RcChannel): Promise<{ ok: boolean; err: string }> {
	const ep = room.t === 'p' ? 'groups.leave' : 'channels.leave';
	const res = await fetch(`${API_BASE}/${ep}`, { method: 'POST', headers: authHeaders(token, userId), body: JSON.stringify({ roomId: room._id }) });
	const d = await res.json().catch(() => ({}));
	if (res.ok && d.success !== false) return { ok: true, err: '' };
	return { ok: false, err: String(d.errorType || d.error || `status ${res.status}`) };
}

export async function rcLeaveRoom(token: string, userId: string, room: RcChannel): Promise<RcLeaveOutcome> {
	const r = await postLeave(token, userId, room);
	if (r.ok) return { status: 'left' };
	// 마지막 방장(error-you-are-last-owner) 또는 archived 방(leave 자체가 거부됨) → 승계/삭제 분기로.
	//   archived 토픽에서 마지막 사용자가 나갈 때 "…is archived" 오류가 그대로 노출되던 문제(2026-07-02).
	if (!/last.?owner|archiv/i.test(r.err)) throw new Error(r.err);
	// 마지막 방장 — 승계 후보 탐색 (모더레이터 우선, 없으면 첫 번째 다른 멤버)
	const [members, roles] = await Promise.all([
		rcRoomMembers(token, userId, room._id, room.t),
		rcRoomRoles(token, userId, room).catch(() => ({} as Record<string, string[]>)),
	]);
	const others = members.filter((m) => m._id !== userId);
	const successor = others.find((m) => (roles[m.username] || []).includes('moderator')) ?? others[0] ?? null;
	return { status: 'last-owner', successor };
}

export async function rcTransferOwnerAndLeave(token: string, userId: string, room: RcChannel, successorId: string): Promise<void> {
	await rcSetRoomRole(token, userId, room, successorId, 'owner', true);
	const r = await postLeave(token, userId, room);
	if (!r.ok) throw new Error(r.err);
}

// 방 삭제는 기존 rcDeleteRoom(위 정의) 재사용. 방 관리자 지정/해제/kick 은 rcSetRoomRole / rcKickUser (위에 이미 존재).
// rcRenameRoom / rcSaveRoomSettings 도 위에 존재.

// 방별 알림 음소거 — rooms.saveNotification (disableNotifications '1'|'0').
// ⭐ 즐겨찾기 토글 — RC subscription.f. 사이드바가 f 를 최상단 정렬·별 아이콘에 쓴다.
//   (2026-08-14: 사이드바 우클릭에만 있던 것을 방 헤더에서도 쓰도록 API 로 정리 — 모바일엔 우클릭이 없다)
export async function rcFavoriteRoom(token: string, userId: string, roomId: string, favorite: boolean): Promise<boolean> {
	const res = await fetch(`${API_BASE}/rooms.favorite`, {
		method: 'POST', headers: authHeaders(token, userId),
		body: JSON.stringify({ roomId, favorite }),
	});
	const d = await res.json().catch(() => ({}));
	if (!res.ok || d.success === false) throw new Error(d.error || d.errorType || `rooms.favorite ${res.status}`);
	return true;
}

export async function rcSetRoomMute(token: string, userId: string, roomId: string, muted: boolean): Promise<boolean> {
	const res = await fetch(`${API_BASE}/rooms.saveNotification`, {
		method: 'POST', headers: authHeaders(token, userId),
		body: JSON.stringify({ roomId, notifications: { disableNotifications: muted ? '1' : '0' } }),
	});
	const d = await res.json().catch(() => ({}));
	if (!res.ok || d.success === false) throw new Error(d.error || d.errorType || `saveNotification ${res.status}`);
	return true;
}

// 방별 알림 3단계 (모두/멘션만/끄기, 2026-07-28) — RC 구독에 저장해 다기기 일관.
//   disableNotifications 도 함께 세팅해 구버전 클라(muted 만 인식)·APK 폴러와 호환 유지.
export type RoomNotifPref = 'all' | 'mentions' | 'nothing';
export async function rcSetRoomNotifPref(token: string, userId: string, roomId: string, pref: RoomNotifPref): Promise<boolean> {
	const res = await fetch(`${API_BASE}/rooms.saveNotification`, {
		method: 'POST', headers: authHeaders(token, userId),
		body: JSON.stringify({ roomId, notifications: {
			desktopNotifications: pref === 'all' ? 'default' : pref,
			disableNotifications: pref === 'nothing' ? '1' : '0',
		} }),
	});
	const d = await res.json().catch(() => ({}));
	if (!res.ok || d.success === false) throw new Error(d.error || d.errorType || `saveNotification ${res.status}`);
	return true;
}

export interface RcMessage {
	_id: string;
	rid: string;
	msg: string;
	ts: string;
	u: { _id: string; username: string; name?: string };
	editedAt?: string;
	_updatedAt?: string;   // RC 메시지 갱신 시각 — 순서역전 병합 가드용
	attachments?: unknown[];
	file?: { _id?: string; name?: string; type?: string };   // 첨부 대표 파일 — msg 없는 파일 메시지의 알림 본문 폴백용
	reactions?: Record<string, { usernames: string[] }>;
	t?: string;           // 시스템 메시지 타입 (uj/au/ru/room_changed_* 등) — 있으면 가운데 안내줄로 렌더
	_pending?: boolean;   // 낙관적 전송 — 서버 확인 전 임시 말풍선(클라이언트 전용)
	// 🧵 RC 스레드(댓글) 필드 (2026-08-26) — 규약은 api/thread.ts. tmid 가 있으면 그 메시지의 댓글,
	//   tshow=true 면 "대화창에도 표시"(Slack 의 Also send to channel). 부모 쪽엔 tcount/tlm/replies 가 선다.
	tmid?: string;
	tshow?: boolean;
	/** 🧵 클라 전용 — 스레드 요약 말풍선(api/thread.ts foldThreadReplies 가 '최근 댓글' 행에 얹음). 서버 필드 아님 */
	_digest?: import('./thread').ThreadDigest;
	tcount?: number;
	tlm?: string;
	replies?: string[];
}

// API base 분기:
// - vite dev (localhost:4000) — 상대 경로 '/api/v1' 사용. vite proxy 가 :3000 으로 forward.
// - tauri release (tauri://localhost 또는 http://tauri.localhost) — proxy 없음, 절대 URL 필요.
//   env VITE_TPT_RC_URL 우선, 없으면 localhost:3000 fallback.
// - 일반 웹 같은 origin 운영 — 상대 경로 유지 (RC 가 reverse-proxy 뒤에 있는 경우).
function detectRcOrigin(): string {
	const envUrl = (import.meta as { env?: Record<string, string> }).env?.VITE_TPT_RC_URL;
	if (envUrl) return envUrl.replace(/\/+$/, '');
	if (typeof window !== 'undefined') {
		const host = window.location.host || '';
		const proto = window.location.protocol || '';
		if (proto === 'tauri:' || /tauri\.localhost/i.test(host)) {
			return 'http://localhost:3000';
		}
	}
	return '';
}
export const RC_ORIGIN = detectRcOrigin();
const API_BASE = `${RC_ORIGIN}/api/v1`;
diagLog('boot RC_ORIGIN=', RC_ORIGIN || '(relative/same-origin)', 'API_BASE=', API_BASE, 'loc=', typeof window !== 'undefined' ? window.location.href : 'n/a');

function authHeaders(token: string, userId: string): HeadersInit {
	return {
		'X-Auth-Token': token,
		'X-User-Id': userId,
		'Content-Type': 'application/json',
	};
}

export type LoginErrorCode =
	| 'invalid_credentials'   // 아이디/비밀번호 불일치
	| 'blocked'               // 계정 차단/잠금
	| 'rate_limited'          // 시도 횟수 초과
	| 'server'                // 서버 5xx
	| 'network'               // 서버 자체에 닿지 못함
	| 'totp_required'         // 🔐 2단계 인증 코드 필요 (TOTP 등록 사용자 — 2026-08-26 D4)
	| 'totp_invalid'          // 🔐 코드 불일치/만료
	| 'unknown';

export class LoginError extends Error {
	code: LoginErrorCode;
	status?: number;
	raw?: string;
	constructor(code: LoginErrorCode, message: string, opts: { status?: number; raw?: string } = {}) {
		super(message);
		this.name = 'LoginError';
		this.code = code;
		this.status = opts.status;
		this.raw = opts.raw;
	}
}

function classifyLoginFailure(status: number, rawMsg: string): LoginErrorCode {
	const msg = (rawMsg || '').toLowerCase();
	// 🔐 RC 2FA — 400 { errorType: 'totp-required' | 'totp-invalid' } (api.ts loginCompatibility → checkCodeForUser)
	if (msg.includes('totp-invalid') || msg.includes('totp invalid')) return 'totp_invalid';
	if (msg.includes('totp-required') || msg.includes('totp required')) return 'totp_required';
	if (status === 401 || status === 403) {
		if (msg.includes('block') || msg.includes('lock') || msg.includes('disabled') || msg.includes('inactive')) return 'blocked';
		// RC 의 표준 인증 실패 — "Unauthorized" / "User not found" 모두 사용자 입력 오류로 통합 표시.
		return 'invalid_credentials';
	}
	if (status === 429 || msg.includes('too many') || msg.includes('rate')) return 'rate_limited';
	if (status >= 500) return 'server';
	if (status === 0) return 'network';
	return 'unknown';
}

// ── 🔐 로그인 비밀번호 digest 전송 (2026-08-12) ──────────────────────────────
// 비밀번호 원문을 요청 본문에 싣지 않는다 — Meteor/RC 표준 { digest: SHA256(pw), algorithm: 'sha-256' }
// 형식으로 전송하면 서버(api/v1/login → loginCompatibility → Meteor login)가 그대로 검증한다.
// crypto.subtle 은 보안 컨텍스트(HTTPS/tauri/localhost) 전용이라, 사내 http://IP 접속을 위해
// 순수 JS SHA-256 폴백을 둔다 (폴백에서도 평문 전송으로 되돌아가지 않음).
function sha256HexFallback(input: string): string {
	const K = [
		0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
		0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
		0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
		0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
		0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
		0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
		0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
		0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
	];
	const H = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];
	const rotr = (x: number, n: number) => (x >>> n) | (x << (32 - n));
	const bytes = new TextEncoder().encode(input);
	const bitLen = bytes.length * 8;
	// 패딩 — 0x80 + 0패딩 + 64bit 길이 (메시지가 2^32 비트를 넘을 일 없음: 상위 워드는 근사치로 충분)
	const padded = new Uint8Array((((bytes.length + 8) >> 6) + 1) << 6);
	padded.set(bytes);
	padded[bytes.length] = 0x80;
	const dv = new DataView(padded.buffer);
	dv.setUint32(padded.length - 8, Math.floor(bitLen / 0x100000000));
	dv.setUint32(padded.length - 4, bitLen >>> 0);
	const w = new Array<number>(64);
	for (let off = 0; off < padded.length; off += 64) {
		for (let i = 0; i < 16; i++) w[i] = dv.getUint32(off + i * 4);
		for (let i = 16; i < 64; i++) {
			const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
			const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
			w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
		}
		let [a, b, c, d, e, f, g, h] = H;
		for (let i = 0; i < 64; i++) {
			const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
			const ch = (e & f) ^ (~e & g);
			const t1 = (h + S1 + ch + K[i] + w[i]) >>> 0;
			const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
			const maj = (a & b) ^ (a & c) ^ (b & c);
			const t2 = (S0 + maj) >>> 0;
			h = g; g = f; f = e; e = (d + t1) >>> 0; d = c; c = b; b = a; a = (t1 + t2) >>> 0;
		}
		H[0] = (H[0] + a) >>> 0; H[1] = (H[1] + b) >>> 0; H[2] = (H[2] + c) >>> 0; H[3] = (H[3] + d) >>> 0;
		H[4] = (H[4] + e) >>> 0; H[5] = (H[5] + f) >>> 0; H[6] = (H[6] + g) >>> 0; H[7] = (H[7] + h) >>> 0;
	}
	return H.map((x) => x.toString(16).padStart(8, '0')).join('');
}

export async function sha256Hex(input: string): Promise<string> {
	if (typeof crypto !== 'undefined' && crypto.subtle) {
		try {
			const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
			return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
		} catch { /* 폴백으로 */ }
	}
	return sha256HexFallback(input);
}

export async function rcLogin(user: string, password: string, totpCode?: string): Promise<RcLoginResult> {
	let res: Response;
	diagLog('login → POST', `${API_BASE}/login`);
	const digest = await sha256Hex(password);
	try {
		// 🔐 2단계 인증 — RC REST login 은 body.code 를 totp.code 로 감싸 Meteor login 에 넘긴다(api.ts loginCompatibility). 백업 코드도 같은 필드.
		res = await fetch(`${API_BASE}/login`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ user, password: { digest, algorithm: 'sha-256' }, ...(totpCode ? { code: totpCode.trim() } : {}) }),
		});
	} catch (e) {
		// fetch 가 throw 하는 경우 — 서버 다운, DNS 실패, CORS 차단, 오프라인 등.
		diagLog('login FETCH FAILED', (e as Error).name, (e as Error).message, '→', `${API_BASE}/login`);
		throw new LoginError('network', `${(e as Error).message || 'network error'} (대상: ${API_BASE}/login)`);
	}
	diagLog('login response', res.status, res.statusText);
	if (!res.ok) {
		const body = await res.json().catch(() => ({} as { error?: string; message?: string; errorType?: string }));
		const raw = body.message || body.error || body.errorType || res.statusText || `HTTP ${res.status}`;
		const code = classifyLoginFailure(res.status, raw);
		throw new LoginError(code, raw, { status: res.status, raw });
	}
	const data = await res.json().catch(() => null) as { status?: string; data?: { authToken: string; userId: string; me: RcLoginResult['me'] }; error?: string; message?: string } | null;
	if (!data || data.status !== 'success' || !data.data) {
		const raw = data?.message || data?.error || 'login failed';
		throw new LoginError('unknown', raw, { status: res.status, raw });
	}
	return {
		authToken: data.data.authToken,
		userId: data.data.userId,
		me: data.data.me,
	};
}

export async function rcLogout(token: string, userId: string): Promise<void> {
	await fetch(`${API_BASE}/logout`, {
		method: 'POST',
		headers: authHeaders(token, userId),
	}).catch(() => undefined);
}

// 429 재시도 공용 — Retry-After 우선, 없으면 exponential backoff. (F5/재연결 burst 시 방 목록이 사라지던 #20/#21)
async function fetchWithRetry(input: string, init: RequestInit, tries = 3): Promise<Response> {
	let res = await fetch(input, init);
	let attempt = 0;
	while (res.status === 429 && attempt < tries) {
		const ra = Number(res.headers.get('Retry-After'));
		const wait = Number.isFinite(ra) && ra > 0 ? Math.min(ra * 1000, 10000) : Math.min(8000, 1000 * Math.pow(2, attempt));
		await new Promise((r) => setTimeout(r, wait));
		res = await fetch(input, init);
		attempt += 1;
	}
	return res;
}

// ⚡ F5 부팅 즉시표시 (SWR, 2026-06-11) — 마지막 정상 방 목록을 동기 반환.
// ChatPage 가 channels 초기값으로 사용해 네트워크 왕복 전에 사이드바를 그린다.
export function rcSubscriptionsPeek(userId: string): RcChannel[] | null {
	const persisted = ssRead<{ ts: number; uid: string; data: RcChannel[] }>(SS_SUBS_KEY);
	return persisted && persisted.uid === userId && Date.now() - persisted.ts < SS_STALE_TTL ? persisted.data : null;
}

export async function rcSubscriptions(token: string, userId: string, opts?: { background?: boolean }): Promise<RcChannel[]> {
	// subscriptions.get 은 unread/alert/open/t/name 만, lastMessage 는 비어있음.
	// rooms.get 은 lastMessage(lm) + ts 보유. 둘을 rid 기준 병합.
	// 🚦 백그라운드 갱신(이벤트·주기)은 429 재시도 안 함 (2026-10-15 부하점검) — RC 호출 제한은 IP+경로 단위(ApiClass.ts rateLimitRule)라
	//   사무실 NAT 1개(14.51.179.254)를 전원이 나눠 쓰고, 거부된 시도도 횟수에 들어가 재시도가 제한을 계속 채웠다(10분 subscriptions.get 45% 429).
	//   다음 이벤트·주기 갱신이 다시 받는다. 첫 로드·사용자 동작은 종전대로 재시도.
	const tries = opts?.background ? 0 : 3;
	const [subRes, roomRes] = await Promise.all([
		fetchWithRetry(`${API_BASE}/subscriptions.get`, { headers: authHeaders(token, userId) }, tries),
		fetchWithRetry(`${API_BASE}/rooms.get`, { headers: authHeaders(token, userId) }, tries),
	]);
	// 백그라운드에선 한쪽만 실패해도 이번 갱신을 건너뛴다 — rooms.get 없이 병합하면 lastMessage·정렬 시각이 빠진 목록으로
	//   사이드바 미리보기·순서가 흔들리고, 옛 캐시 폴백은 화면의 최신 상태를 되돌린다. 화면은 지금 상태 그대로 둔다.
	if (opts?.background && (!subRes.ok || !roomRes.ok)) throw new Error(`subscriptions skipped: ${subRes.status}/${roomRes.status}`);
	if (!subRes.ok) {
		// F5 연타 등으로 429 가 재시도 후에도 안 풀린 경우 — 마지막 정상 방 목록으로 폴백 (빈 사이드바 방지)
		if (subRes.status === 429) {
			const persisted = ssRead<{ ts: number; uid: string; data: RcChannel[] }>(SS_SUBS_KEY);
			if (persisted && persisted.uid === userId && Date.now() - persisted.ts < SS_STALE_TTL) {
				console.warn('[rcSubscriptions] 429 retry exhausted; serving sessionStorage stale cache');
				return persisted.data;
			}
		}
		throw new Error(`subscriptions failed: ${subRes.status}`);
	}
	const subData = await subRes.json();
	const roomData = roomRes.ok ? await roomRes.json() : { update: [] };
	const roomById = new Map<string, Record<string, unknown>>();
	for (const r of (roomData.update ?? []) as Array<Record<string, unknown>>) {
		roomById.set(r._id as string, r);
	}
	// RC subscription doc 의 _id 는 subscription ID 이고 진짜 room ID 는 rid.
	const merged = (subData.update ?? []).map((s: { rid?: string; _id: string } & Record<string, unknown>) => {
		const rid = s.rid ?? s._id;
		const room = roomById.get(rid);
		const lastMessage = room?.lastMessage as RcChannel['lastMessage'] | undefined;
		const dn = (s as { disableNotifications?: unknown }).disableNotifications;
		const dnp = (s as { desktopNotifications?: unknown }).desktopNotifications;
		const isMuted = dn === '1' || dn === 1 || dn === true;
		// 🧮 삭제로 남은 유령 안읽음 보정 (2026-08-26 사용자: 상대가 보내놓고 지웠는데 숫자가 안 사라진다) —
		//   RC 는 일반 메시지를 지워도 subscription.unread 를 줄이지 않는다(스레드 답글만 정리한다 —
		//   apps/meteor/app/lib/server/functions/deleteMessage.ts). 그래서 상대가 보내고 바로 지우면
		//   배지 숫자만 남는다. 마지막 메시지 시각이 내가 읽은 시각(ls)보다 이르면 안 읽은 메시지가
		//   남아 있을 수 없으므로 0 으로 본다. 여러 건 중 일부만 지운 경우는 마지막이 ls 뒤라 서버 값을 그대로 쓴다.
		//   🔴 기준은 `room.lm` 이 아니라 **`room.lastMessage.ts`** (2026-09-08 사용자 "상대가 지웠는데 안읽음이 안 없어진다" 재발):
		//   RC `deleteMessage` 는 마지막 메시지를 지우면 `Rooms.resetLastMessageById` 로 lastMessage 만 앞 메시지로 되돌리고
		//   `lm` 은 건드리지 않는다(packages/models Rooms.ts) → lm 비교는 영원히 참이 안 됐다. lastMessage 가 없으면 lm 폴백.
		const lsTs = Date.parse(String((s as { ls?: unknown }).ls ?? ''));
		const lmTs = Date.parse(String((lastMessage as { ts?: string } | undefined)?.ts ?? (room?.lm as string) ?? ''));
		const rawUnread = Number((s as { unread?: unknown }).unread) || 0;
		const ghostUnread = rawUnread > 0 && Number.isFinite(lsTs) && Number.isFinite(lmTs) && lmTs <= lsTs;
		return {
			...s,
			_id: rid,
			muted: isMuted,
			...(ghostUnread ? { unread: 0, userMentions: 0 } : {}),
			// 알림 3단계 — desktopNotifications('mentions'|'nothing') 우선, 레거시 muted 는 'nothing' 취급
			notifPref: (dnp === 'mentions' ? 'mentions' : (dnp === 'nothing' || isMuted) ? 'nothing' : 'all') as RcChannel['notifPref'],
			lastMessage: lastMessage ?? (s.lastMessage as RcChannel['lastMessage'] | undefined),
			usersCount: (room?.usersCount as number | undefined) ?? (s as { usersCount?: number }).usersCount,   // 방 선택 모달 참여자 수 표기 (2026-08-29)
			// 🏷 방 문서 속성 — subscription 에는 없고 rooms.get 에만 있는 필드. 종전엔 병합에서 빠져 방 전환(refreshSubs) 뒤 topic·description 이 사라졌다
			//   (2026-09-13 사용자 "PJT 제목 저장하고 다른 방 다녀오니 사라짐"). onRoomPatched 즉시 패치는 activeRoom 만 살리고 다음 목록 갱신이 덮어썼다.
			...(room?.topic !== undefined ? { topic: room.topic as string } : {}),
			...(room?.description !== undefined ? { description: room.description as string } : {}),
			...(room?.announcement !== undefined ? { announcement: room.announcement as string } : {}),
			...(room?.ro !== undefined ? { ro: room.ro as boolean } : {}),
			updatedAt: (room?.lm as string) || (room?._updatedAt as string) || (s.updatedAt as string) || (s._updatedAt as string),
		};
	}) as RcChannel[];
	// 🛡 open === false 필터 (2026-06-11) — im.close/조용히 나가기(hide)는 sub 을 지우지 않고
	// open=false 로만 바꾸므로, 필터 없이는 나간 DM/숨긴 방이 사이드바에 계속 남는다.
	const visible = merged.filter((s) => (s as { open?: boolean }).open !== false);
	// 🔥 내용이 같으면 저장하지 않는다 (2026-09-30 과열 점검) — 방 목록 전체를 매번 동기 localStorage 에 쓰던 것.
	//   직렬화 비교는 메모리에서만(ts 제외한 data 문자열), 실제 디스크 쓰기는 바뀐 경우에만.
	try {
		const dataStr = JSON.stringify(visible);
		const sig = `${userId}|${dataStr}`;
		const now = Date.now();
		// 같아도 30분마다는 다시 쓴다 — ts 가 24h TTL(SS_STALE_TTL)을 넘겨 콜드 즉시표시가 끊기지 않게
		if (sig !== _subsCacheSig || now - _subsCacheAt > 30 * 60_000) {
			cacheStorage.setItem(SS_SUBS_KEY, `{"ts":${now},"uid":${JSON.stringify(userId)},"data":${dataStr}}`);
			_subsCacheSig = sig; _subsCacheAt = now;
		}
	} catch { /* quota — best-effort */ }
	return visible;
}

// 🛡 429 mitigation —
//  1) 동일 (endpoint, roomId, latest, count) in-flight 호출 공유 (탭 복귀/WS 재연결 등으로 중복 발사 차단).
//  2) 직전 성공 결과를 5초 캐시 — 같은 query 가 5초 안에 다시 들어오면 fetch X.
//  3) 429 응답 시 exponential backoff 으로 최대 3회 재시도. Retry-After 헤더 우선.
//  4) 직전 429 가 떴던 url 은 60초간 stale 캐시 우선 반환 (UX — 사용자가 빈 채팅창 보지 않게).
const histInflight = new Map<string, Promise<RcMessage[]>>();
const histCache = new Map<string, { ts: number; data: RcMessage[] }>();
const HIST_CACHE_TTL = 5000;
const HIST_STALE_TTL = 60000; // 429 회복 동안 stale 허용
const HIST_CACHE_MAX = 80;    // 🛡 무제한 증가 방지 — 방 80개분 초과 시 가장 오래된 항목 evict (2026-06-10)
// 방별 캐시 세대 — clearRoomHistoryCache 가 올린다. 무효화 전에 출발한 조회가 늦게 끝나도 결과를 캐시에 쓰지 않는다 (2026-10-15)
const histGen = new Map<string, number>();

// 🛡 F5 생존 stale 캐시 (2026-06-11) — 위 메모리 캐시는 새로고침에 전부 소실되어
// F5 연타 시 429 보호가 무력화됨 ("메시지를 불러올 수 없습니다" + 빈 화면).
// 첫 페이지(history latest 없음) 결과와 방 목록을 sessionStorage 에 best-effort 영속해,
// 429 가 끝까지 안 풀릴 때 마지막 정상 데이터로 화면을 채운다 (WS 재연결 후 실시간 보정됨).
const SS_HIST_KEY = 'tpt-hist-cache-v1';
const SS_SUBS_KEY = 'tpt-subs-cache-v1';
// 🔥 마지막으로 디스크에 쓴 방 목록 서명·시각 — 같은 내용 반복 쓰기 생략용 (2026-09-30 과열 점검)
let _subsCacheSig = '';
let _subsCacheAt = 0;
// ⚡ 콜드 스타트 즉시표시 — localStorage 영속이라 앱/브라우저 재시작 후에도 캐시 생존.
//   24시간까지 stale 즉시표시 허용(표시 직후 네트워크 최신본으로 교체 = SWR). 메신저 체감속도 핵심.
const SS_STALE_TTL = 24 * 60 * 60_000;
const SS_HIST_MAX = 15;           // quota 보호 — 최근 방 15개분만

// 영속 캐시 저장소 — localStorage(앱/브라우저 재시작 후에도 생존) 우선, 불가 시 sessionStorage 폴백.
//   기존 sessionStorage 는 탭/앱 종료 시 소실되어 콜드 스타트마다 빈 화면 → localStorage 로 콜드 즉시표시.
const cacheStorage: Storage = (() => {
	try { const k = '__tpt_cache_t__'; localStorage.setItem(k, '1'); localStorage.removeItem(k); return localStorage; } catch { /* */ }
	try { return sessionStorage; } catch { /* */ }
	return { getItem: () => null, setItem: () => undefined, removeItem: () => undefined, clear: () => undefined, key: () => null, length: 0 } as unknown as Storage;
})();

// 특정 방의 히스토리 메모리 캐시 무효화 — 삭제/수정 직후 reload 가 stale(삭제 전) 캐시를 받지 않도록.
//   키 형식: `endpoint|roomId|latest|oldest|count` → split('|')[1] === roomId.
export function clearRoomHistoryCache(roomId: string): void {
	if (!roomId) return;
	for (const k of [...histCache.keys()]) {
		if (k.split('|')[1] === roomId) histCache.delete(k);
	}
	// 진행 중 요청도 떼어 낸다 (2026-10-15) — 수정·반응 직전에 출발한 조회를 이어 받으면 수정 전 본문이 다시 그려진다.
	//   요청 자체는 계속 돌지만 다음 호출은 공유하지 않고 새로 나가며, 세대가 바뀌어 늦은 결과는 캐시에 남지 않는다.
	for (const k of [...histInflight.keys()]) {
		if (k.split('|')[1] === roomId) histInflight.delete(k);
	}
	histGen.set(roomId, (histGen.get(roomId) || 0) + 1);
}

// 🗑 메시지 삭제 알림 (2026-08-20) — 캐시 무효화 + `tpt:message-deleted` 발사.
//   근거: 삭제 후 호출하는 reloadMessages 는 **증분 동기화(최신 ts 이후만 append)** 라 지운 말풍선을
//   화면에서 치우지 못한다. WS(stream-notify-room `{rid}/deleteMessage`)가 오면 지워지지만, 연결이
//   끊긴 상태(모바일 웹뷰 백그라운드 복귀 직후 등)에서는 새로고침해야 사라졌다 (사용자 신고).
//   → 삭제한 쪽이 이 함수를 부르고, 목록을 소유한 쪽(useChatRooms)이 즉시 제거한다. WS 가 뒤늦게
//   같은 삭제를 알려도 이미 없는 _id 라 무해(idempotent).
export function notifyMessageDeleted(roomId: string, msgId: string): void {
	if (!roomId || !msgId) return;
	clearRoomHistoryCache(roomId);
	try { window.dispatchEvent(new CustomEvent('tpt:message-deleted', { detail: { roomId, msgId } })); } catch { /* SSR/비브라우저 */ }
}

function ssRead<T>(key: string): T | null {
	try { return JSON.parse(cacheStorage.getItem(key) || 'null') as T | null; } catch { return null; }
}
// 🔒 캐시 항목에 uid 를 함께 저장하고 읽을 때 대조 — 같은 탭에서 다른 계정으로
// 재로그인한 직후 429 가 나면 이전 사용자 데이터가 보일 수 있는 누수 차단.
function ssHistWrite(key: string, uid: string, data: RcMessage[]) {
	try {
		const all = ssRead<Record<string, { ts: number; uid: string; data: RcMessage[] }>>(SS_HIST_KEY) || {};
		delete all[key]; // 재삽입으로 최신 순서 유지 (객체 키 삽입 순서 = FIFO)
		all[key] = { ts: Date.now(), uid, data };
		const keys = Object.keys(all);
		while (keys.length > SS_HIST_MAX) { delete all[keys[0]]; keys.shift(); }
		cacheStorage.setItem(SS_HIST_KEY, JSON.stringify(all));
	} catch { /* quota/프라이빗 모드 — 영속 캐시는 best-effort */ }
}
function ssHistRead(key: string, uid: string): RcMessage[] | null {
	const entry = (ssRead<Record<string, { ts: number; uid: string; data: RcMessage[] }>>(SS_HIST_KEY) || {})[key];
	return entry && entry.uid === uid && Date.now() - entry.ts < SS_STALE_TTL ? entry.data : null;
}

async function fetchHistory(
	endpoint: 'channels.history' | 'im.history' | 'groups.history',
	token: string,
	userId: string,
	roomId: string,
	count: number,
	latest?: string,
	oldest?: string,
): Promise<RcMessage[]> {
	const url = new URL(`${API_BASE}/${endpoint}`, window.location.origin);
	url.searchParams.set('roomId', roomId);
	url.searchParams.set('count', String(count));
	// 🧵 댓글도 함께 받는다 (2026-09-07 "모든 대화내역 표시") — channels/im 은 기본 제외, groups 는 기본 포함이라 명시해 세 곳을 같게.
	url.searchParams.set('showThreadMessages', 'true');
	if (latest) url.searchParams.set('latest', latest);
	// ⚡ 증분 동기화(delta sync) — oldest 를 주면 그 시각 "이후"(자기 자신 제외) 메시지만 받는다.
	//   재연결/탭복귀 시 보유한 최신 메시지 ts 를 oldest 로 넘겨 놓친 것만 보충 (전체 재요청 대신).
	if (oldest) {
		url.searchParams.set('oldest', oldest);
		url.searchParams.set('inclusive', 'false');
	}
	const key = `${endpoint}|${roomId}|${latest || ''}|${oldest || ''}|${count}`;
	// 5초 결과 캐시
	const cached = histCache.get(key);
	if (cached && Date.now() - cached.ts < HIST_CACHE_TTL) return cached.data;
	// in-flight dedup
	const pending = histInflight.get(key);
	if (pending) return pending;
	const gen = histGen.get(roomId) || 0;
	const exec = (async () => {
		// ⏱ 15초 타임아웃 (2026-10-15 사용자 신고 "아침에 어제 것까지만 보이고 오늘 것은 안 보임, 재부팅하니 보임") —
		//   절전 복귀 직후 죽은 연결에 걸린 요청은 응답도 오류도 없이 영원히 대기한다. 아래 in-flight 공유와 겹치면
		//   같은 방의 이후 요청이 전부 그 멈춘 요청을 기다려 캐시(어제분)만 보였다. 끊어야 다음 요청이 새로 나간다.
		const doFetch = () => fetch(url.pathname + url.search, { headers: authHeaders(token, userId), signal: AbortSignal.timeout(15000) });
		// 최대 3회 재시도. backoff: Retry-After 우선, 없으면 1s/2s/4s exponential.
		let res = await doFetch();
		let attempt = 0;
		while (res.status === 429 && attempt < 3) {
			const ra = Number(res.headers.get('Retry-After'));
			const expo = Math.min(8000, 1000 * Math.pow(2, attempt));
			const wait = Number.isFinite(ra) && ra > 0 ? Math.min(ra * 1000, 10000) : expo;
			await new Promise((r) => setTimeout(r, wait));
			res = await doFetch();
			attempt += 1;
		}
		if (res.status === 429) {
			// 끝까지 429 — stale cache 가 있으면 그걸 반환 (UX 보호)
			if (cached && Date.now() - cached.ts < HIST_STALE_TTL) {
				console.warn(`[fetchHistory] 429 retry exhausted; serving stale cache (age ${Math.round((Date.now() - cached.ts) / 1000)}s)`);
				return cached.data;
			}
			// F5 리로드로 메모리 캐시가 소실된 경우 — sessionStorage 영속본 폴백
			const persisted = ssHistRead(key, userId);
			if (persisted) {
				console.warn('[fetchHistory] 429 retry exhausted; serving sessionStorage stale cache');
				return persisted;
			}
			throw new Error(`${endpoint} failed: 429 (rate limit — stale cache 없음)`);
		}
		if (!res.ok) throw new Error(`${endpoint} failed: ${res.status}`);
		const data = await res.json();
		const msgs: RcMessage[] = data.messages ?? [];
		if ((histGen.get(roomId) || 0) !== gen) return msgs;   // 출발 뒤 무효화됨 — 결과는 돌려주되 캐시에는 쓰지 않는다
		// 🛡 캐시 크기 cap — Map 은 삽입 순서 보존 → 가장 오래된 키부터 삭제 (FIFO)
		while (histCache.size >= HIST_CACHE_MAX) {
			const oldest = histCache.keys().next().value;
			if (oldest === undefined) break;
			histCache.delete(oldest);
		}
		histCache.set(key, { ts: Date.now(), data: msgs });
		if (!latest && !oldest) ssHistWrite(key, userId, msgs); // 첫 페이지만 F5 생존 영속 (delta·페이지네이션 제외, quota 보호)
		return msgs;
	})();
	histInflight.set(key, exec);
	try {
		return await exec;
	} finally {
		if (histInflight.get(key) === exec) histInflight.delete(key);   // 무효화 뒤 새로 나간 같은 키 요청은 건드리지 않는다
	}
}

export function rcChannelHistory(token: string, userId: string, roomId: string, count = 50, latest?: string) {
	return fetchHistory('channels.history', token, userId, roomId, count, latest);
}

export function rcImHistory(token: string, userId: string, roomId: string, count = 50, latest?: string) {
	return fetchHistory('im.history', token, userId, roomId, count, latest);
}

export function rcGroupHistory(token: string, userId: string, roomId: string, count = 50, latest?: string) {
	return fetchHistory('groups.history', token, userId, roomId, count, latest);
}

// 메시지 정책 — 관리자 '메시지 정책'(RC settings)에서 설정한 삭제/수정 허용·시간제한.
//   클라가 이 값을 읽어 메뉴의 삭제/수정 버튼을 노출/숨김(차단 시 버튼 자체를 숨김). 60초 캐시.
export interface MsgPolicy { allowDelete: boolean; deleteBlockMin: number; allowEdit: boolean; editBlockMin: number }
let _msgPolicy: { ts: number; data: MsgPolicy } | null = null;
export async function rcMessagePolicy(token: string, userId: string): Promise<MsgPolicy> {
	const def: MsgPolicy = { allowDelete: true, deleteBlockMin: 0, allowEdit: true, editBlockMin: 0 };
	if (_msgPolicy && Date.now() - _msgPolicy.ts < 60_000) return _msgPolicy.data;
	try {
		const ids = ['Message_AllowDeleting', 'Message_AllowDeleting_BlockDeleteInMinutes', 'Message_AllowEditing', 'Message_AllowEditing_BlockEditInMinutes'];
		const q = encodeURIComponent(JSON.stringify({ _id: { $in: ids } }));
		const r = await fetch(`${API_BASE}/settings.public?query=${q}&count=20`, { headers: authHeaders(token, userId) });
		if (!r.ok) return def;
		const j = await r.json();
		const map: Record<string, unknown> = {};
		for (const s of (j.settings || []) as Array<{ _id: string; value: unknown }>) map[s._id] = s.value;
		const data: MsgPolicy = {
			allowDelete: map.Message_AllowDeleting !== false,
			deleteBlockMin: Number(map.Message_AllowDeleting_BlockDeleteInMinutes || 0),
			allowEdit: map.Message_AllowEditing !== false,
			editBlockMin: Number(map.Message_AllowEditing_BlockEditInMinutes || 0),
		};
		_msgPolicy = { ts: Date.now(), data };
		return data;
	} catch { return def; }
}

// 📎 첨부 용량 정책 (2026-08-18 사용자) — 관리자 '메시지 정책'의 `FileUpload_MaxFileSize`(바이트).
//   서버가 초과분을 거부하기 전에 클라가 먼저 걸러 "왜 안 올라가는지" 를 알려준다. 60초 캐시.
//   기본값 1GB — 설정 조회 실패 시 폴백. RC 규약상 0 이하는 '무제한'(Infinity)로 본다.
export const DEFAULT_MAX_UPLOAD_BYTES = 1024 * 1024 * 1024;
let _uploadPolicy: { ts: number; max: number } | null = null;
export async function rcMaxUploadBytes(token: string, userId: string): Promise<number> {
	if (_uploadPolicy && Date.now() - _uploadPolicy.ts < 60_000) return _uploadPolicy.max;
	try {
		const q = encodeURIComponent(JSON.stringify({ _id: { $in: ['FileUpload_MaxFileSize'] } }));
		const r = await fetch(`${API_BASE}/settings.public?query=${q}&count=5`, { headers: authHeaders(token, userId) });
		if (!r.ok) return DEFAULT_MAX_UPLOAD_BYTES;
		const j = await r.json();
		const row = ((j.settings || []) as Array<{ _id: string; value: unknown }>).find((s) => s._id === 'FileUpload_MaxFileSize');
		const raw = Number(row?.value);
		if (!Number.isFinite(raw)) return DEFAULT_MAX_UPLOAD_BYTES;
		const max = raw <= 0 ? Infinity : raw;
		_uploadPolicy = { ts: Date.now(), max };
		return max;
	} catch { return DEFAULT_MAX_UPLOAD_BYTES; }
}

// ✍️ 메시지 글자 수 정책 (2026-09-18 사용자 "1만자로 올렸는데 5000자에서 막힘") — 관리자 '메시지 정책'의 `Message_MaxAllowedSize`.
//   종전엔 클라가 RC 기본값 5000 을 상수로 박아 두어, 관리자가 값을 올려도 입력창·전송 전 검사에서만 5000 으로 막혔다
//   (RC 서버는 설정값을 그대로 쓰므로 실제 한도와 화면이 어긋남). 이제 설정을 읽어 그대로 따른다. 60초 캐시.
//   🔴 상한을 따로 두지 않는다 — RC 전송 API(chat.sendMessage)가 이 설정으로 판정하므로 클라가 더 조이면
//      "관리자가 올렸는데 안 된다" 가 재발한다. 값이 없거나 0 이하·비정상이면 RC 기본값 5000 으로 폴백한다
//      (RC 는 `len > maxAllowedSize` 로만 검사해 0 이면 전송이 전부 막히므로 0 을 '무제한'으로 보면 안 된다).
export const DEFAULT_MAX_MSG_LEN = 5000;
let _msgLenPolicy: { ts: number; max: number } | null = null;
export async function rcMaxMessageLength(token: string, userId: string): Promise<number> {
	if (_msgLenPolicy && Date.now() - _msgLenPolicy.ts < 60_000) return _msgLenPolicy.max;
	try {
		const q = encodeURIComponent(JSON.stringify({ _id: { $in: ['Message_MaxAllowedSize'] } }));
		const r = await fetch(`${API_BASE}/settings.public?query=${q}&count=5`, { headers: authHeaders(token, userId) });
		if (!r.ok) return DEFAULT_MAX_MSG_LEN;
		const j = await r.json();
		const row = ((j.settings || []) as Array<{ _id: string; value: unknown }>).find((s) => s._id === 'Message_MaxAllowedSize');
		const raw = Math.floor(Number(row?.value));
		if (!Number.isFinite(raw) || raw <= 0) return DEFAULT_MAX_MSG_LEN;
		_msgLenPolicy = { ts: Date.now(), max: raw };
		return raw;
	} catch { return DEFAULT_MAX_MSG_LEN; }
}

export async function rcRoomHistory(
	token: string,
	userId: string,
	room: RcChannel,
	count = 50,
	latest?: string,
): Promise<RcMessage[]> {
	if (room.t === 'c') return rcChannelHistory(token, userId, room._id, count, latest);
	if (room.t === 'd') return rcImHistory(token, userId, room._id, count, latest);
	if (room.t === 'p') return rcGroupHistory(token, userId, room._id, count, latest);
	return [];
}

// ⚡ 증분 동기화(delta sync) — sinceTs(보유한 최신 메시지 ts) "이후" 메시지만 받는다.
//   재연결/탭복귀 시 전체 재요청 대신 놓친 것만 보충하기 위함 (네트워크·429 부담↓).
//   RC 는 최신순(desc)으로 최대 count 개 반환 — 호출부에서 dedup·정렬·append 한다.
export async function rcRoomHistorySince(
	token: string,
	userId: string,
	room: RcChannel,
	sinceTs: string,
	count = 50,
): Promise<RcMessage[]> {
	const endpoint = room.t === 'c' ? 'channels.history' : room.t === 'd' ? 'im.history' : room.t === 'p' ? 'groups.history' : null;
	if (!endpoint) return [];
	return fetchHistory(endpoint, token, userId, room._id, count, undefined, sinceTs);
}

// 🗓 시점 '다음' 메시지 — afterTs 보다 늦은 것을 **오래된 순으로** count 개 (2026-10-19 형 "날짜로 이동하면 그 날~오늘 사이 대화가 안 보임").
//   🔴 rcRoomHistorySince(*.history?oldest=)는 그 시각 이후 중 **최신 count 개**(내림차순 상한)를 준다 — 사이가 count 보다 많으면
//   [앵커 이전 한 페이지] + [오늘 근처 한 페이지] 로 붙고 가운데가 통째로 빠졌다(아래로 더 불러오기도 '이미 최신' 으로 멈춤).
//   '앵커 바로 다음' 이 필요한 곳(날짜 이동·아래 방향 페이징·안읽음 진입)은 이 함수. 재연결 delta 는 '최신' 이 맞으니 종전 함수 그대로.
//   RC 메서드 loadNextMessages(rid, end, limit) = ts > end 오름차순(댓글 포함 — history showThreadMessages=true 와 같은 범위).
//   DDP EJSON 날짜는 REST 모양(ISO 문자열)으로 푼다. 실패하면 종전 함수로 폴백(가운데가 빌 수는 있어도 화면은 뜬다).
export async function rcRoomMessagesAfter(token: string, userId: string, room: RcChannel, afterTs: string, count = 50): Promise<RcMessage[]> {
	const end = Date.parse(afterTs);
	if (!Number.isFinite(end)) return [];
	try {
		const res = await fetch(`${API_BASE}/method.call/loadNextMessages`, {
			method: 'POST', headers: authHeaders(token, userId),
			body: JSON.stringify({ message: JSON.stringify({ msg: 'method', id: String(Date.now()), method: 'loadNextMessages', params: [room._id, { $date: end }, count] }) }),
			signal: AbortSignal.timeout(15000),
		});
		const outer = await res.json().catch(() => ({})) as { success?: boolean; message?: string };
		if (!res.ok || outer.success === false || !outer.message) throw new Error(`loadNextMessages ${res.status}`);
		const inner = JSON.parse(outer.message) as { result?: { messages?: unknown[] }; error?: { reason?: string; error?: string } };
		if (inner.error || !Array.isArray(inner.result?.messages)) throw new Error(`loadNextMessages ${inner.error?.reason || inner.error?.error || 'no result'}`);
		const list = reviveEjsonDates(inner.result.messages) as RcMessage[];
		// 방별 숨김 시스템 메시지(room.sysMes 배열) — REST history 는 서버가 거르지만 이 메서드는 안 거른다. 운영 1개 방만 해당(2026-10-19 실측)
		const hidden = (room as RcChannel & { sysMes?: unknown }).sysMes;
		return Array.isArray(hidden) ? list.filter((m) => !m.t || !hidden.includes(m.t)) : list;
	} catch (e) {
		console.warn('[rcRoomMessagesAfter] 폴백(history oldest):', (e as Error).message);
		return rcRoomHistorySince(token, userId, room, afterTs, count);
	}
}

// ✏ 수정·삭제 동기화 (2026-10-15) — since 이후 바뀐 메시지(수정·반응·댓글 메타)와 지워진 _id 를 받는다.
//   ts 기준 delta(rcRoomHistorySince)는 새 메시지만 잡아, 실시간 연결이 끊긴 동안의 수정이 영영 반영되지 않았다.
//   RC 표준 chat.syncMessages 의 **커서 모드**(type=UPDATED/DELETED, _updatedAt 오름차순, count 상한) —
//   무페이지 모드(lastUpdate)는 서버에 상한이 없어 붐비는 방·긴 공백이면 수천 건이 한 번에 온다(1500명 × 창 복귀마다).
//   반환: until = 받은 것 중 가장 늦은 변경 시각(다음 기준), more = 상한에 걸려 남은 게 있음. 실패하면 null.
export const SYNC_PAGE = 100;
export async function rcSyncMessages(
	token: string,
	userId: string,
	roomId: string,
	since: string,
): Promise<{ updated: RcMessage[]; deleted: string[]; until: string; more: boolean } | null> {
	const sinceMs = Date.parse(since);
	if (!Number.isFinite(sinceMs)) return null;
	const page = async (type: 'UPDATED' | 'DELETED') => {
		const qs = `roomId=${encodeURIComponent(roomId)}&type=${type}&next=${sinceMs}&count=${SYNC_PAGE}`;
		const res = await fetch(`${API_BASE}/chat.syncMessages?${qs}`, { headers: authHeaders(token, userId), signal: AbortSignal.timeout(15000) });
		if (!res.ok) throw new Error(`chat.syncMessages ${type} ${res.status}`);
		const data = await res.json();
		return data?.result as { updated?: RcMessage[]; deleted?: Array<{ _id?: string; _deletedAt?: string }>; cursor?: { next?: string | null } };
	};
	try {
		const [u, d] = await Promise.all([page('UPDATED'), page('DELETED')]);
		const updated = Array.isArray(u?.updated) ? u.updated : [];
		const deletedRows = Array.isArray(d?.deleted) ? d.deleted : [];
		const maxOf = (ts: Array<string | undefined>) => ts.reduce((mx, s) => { const t = Date.parse(s || ''); return t > mx ? t : mx; }, sinceMs);
		const uMax = maxOf(updated.map((m) => m._updatedAt));
		const dMax = maxOf(deletedRows.map((x) => x._deletedAt));
		const uMore = !!u?.cursor?.next;
		const dMore = !!d?.cursor?.next;
		// 다음 기준 — 한쪽이 상한에 걸렸으면 그쪽 마지막 시각까지만 전진(남은 것을 건너뛰지 않게)
		const all = Math.max(uMax, dMax);
		const untilMs = Math.min(uMore ? uMax : all, dMore ? dMax : all);
		return {
			updated,
			deleted: deletedRows.map((x) => x._id || '').filter(Boolean),
			until: new Date(untilMs).toISOString(),
			more: uMore || dMore,
		};
	} catch { return null; }
}

// ⚡ 방 전환 체감속도 (SWR, 2026-06-11) — 마지막으로 받아둔 첫 페이지를 동기 반환.
// ChatPage 가 이걸로 즉시 그리고, rcRoomHistory 최신본이 오면 교체한다.
// 메모리 캐시(현 세션) → sessionStorage(F5 생존) 순. 둘 다 10분 TTL.
export function rcRoomHistoryPeek(userId: string, room: RcChannel, count = 50): RcMessage[] | null {
	const endpoint = room.t === 'c' ? 'channels.history' : room.t === 'd' ? 'im.history' : room.t === 'p' ? 'groups.history' : null;
	if (!endpoint) return null;
	// ⚠ write 키(fetchHistory)는 `endpoint|roomId|<latest>|<oldest>|count` — 첫 페이지는 latest/oldest 빈값이라 파이프 3개.
	//   기존 peek 키가 파이프 2개라 항상 캐시 미스 → 방 전환마다 네트워크 대기(느림). write 키와 정확히 일치시킨다.
	const key = `${endpoint}|${room._id}|||${count}`;
	const mem = histCache.get(key);
	if (mem && Date.now() - mem.ts < SS_STALE_TTL) return mem.data;
	return ssHistRead(key, userId);
}

// 읽음 처리 — ⚠️ 실패를 조용히 삼키면 호출측 스팸 가드(같은 마지막 메시지 1회 발사)와 결합해
//   배지 1 이 영구 잔존한다(2026-08-04 실사례 — 네트워크/레이트리밋 일시 실패). 1회 재시도 + 성공 여부 반환.
export async function rcMarkRead(
	token: string,
	userId: string,
	roomId: string,
): Promise<boolean> {
	for (let attempt = 0; attempt < 2; attempt++) {
		try {
			const res = await fetch(`${API_BASE}/subscriptions.read`, {
				method: 'POST',
				headers: authHeaders(token, userId),
				// readThreads 는 넣지 않는다 — [대화창에 표시] 해제 댓글의 tunread 는 패널을 열어야 읽음. 표시된(요약) 스레드는 MessageView 가
				//   방 읽음 때 rcReadThread 로 골라 지운다 (2026-09-07)
				body: JSON.stringify({ rid: roomId }),
			});
			if (res.ok) return true;
		} catch { /* 네트워크 일시 오류 — 재시도 */ }
		if (attempt === 0) await new Promise((r) => setTimeout(r, 800));
	}
	return false;
}

// 본인 비밀번호 변경 — RC users.updateOwnBasicInfo. 준회원 온보딩(첫 로그인 비번 변경)·설정에서 공용.
export async function rcUpdateOwnPassword(
	token: string,
	userId: string,
	currentPassword: string,
	newPassword: string,
): Promise<void> {
	// currentPassword 는 RC 표준(PasswordCheckFallback)이 기대하는 SHA-256 digest 로 전송 — 원문 비노출.
	// newPassword 는 서버가 정책검증+해시 저장해야 해서 원문 필수 (TLS 보호).
	const res = await fetch(`${API_BASE}/users.updateOwnBasicInfo`, {
		method: 'POST',
		headers: authHeaders(token, userId),
		body: JSON.stringify({ data: { currentPassword: await sha256Hex(currentPassword), newPassword } }),
	});
	const d = await res.json().catch(() => ({}));
	if (!res.ok || d.success === false) throw new Error(d.error || d.errorType || `updateOwnBasicInfo ${res.status}`);
	await rcSyncPwFlagAfterChange(token, userId);
}

// 🔐 비번을 바꾼 **직후** 임시비번 플래그 정리 — 운영 RC 6.5 는 본인 변경(updateOwnBasicInfo)이
//   requirePasswordChange 를 해제하지 않는다(해제 코드는 RC 7.10+). 그래서 비번만 바뀌고 플래그가 남아
//   일일 점검(pwflag-audit)이 "변경됐는데 잔류"로 잡아 뒤늦게 치우고 있었다
//   (2026-09-02 원격 "왜 자꾸 발생하지?" — 실측 4명 전원 tpt_temp_pw.clearedAt 없음 = 강제화면이 아닌
//    개인설정 경로로 바꾼 사람들. 전체로도 발급 213건 중 89건이 pw-set 을 안 거쳤다).
//   /auth/pw-force 가 발급 시점 해시와 비교해 다르면 플래그를 $unset 한다 — 플래그가 없으면 그냥 no-op.
//   실패해도 사용자 흐름을 막지 않는다(다음 로그인 또는 일일 점검이 결국 정리).
export async function rcSyncPwFlagAfterChange(token: string, userId: string): Promise<void> {
	try { await rcPwForceCheck(token, userId); } catch { /* 정리 실패는 조용히 — 뒤에 두 겹의 그물이 있다 */ }
}

// 🔐 임시비번 강제변경 판정 (2026-08-25) — RC 의 requirePasswordChange 플래그만으로는 출처(임시비번/신규계정/잔재)를 알 수 없어
//   사이드카가 비번찾기 발급 시각(48h 창)으로 판정한다. 창 밖 플래그는 서버가 정리. 사이드카 미도달 시엔 종전 동작(강제) 유지 —
//   임시비번 하나 더 바꾸게 하는 쪽이 새 실패 모드를 만드는 것보다 낫다.
export async function rcPwForceCheck(token: string, userId: string): Promise<{ force: boolean; issuedAt: string | null }> {
	try {
		const r = await fetch('/api/tpt/auth/pw-force', { headers: { 'X-Auth-Token': token, 'X-User-Id': userId } });
		if (!r.ok) return { force: true, issuedAt: null };
		const j = (await r.json().catch(() => ({}))) as { force?: boolean; issuedAt?: unknown };
		// issuedAt = 임시비번 발급 시각 — LoginPage 의 기기 로컬 우회(변경 기록·유예)가 재발급 이후인지 판별하는 기준
		return { force: j.force !== false, issuedAt: typeof j.issuedAt === 'string' ? j.issuedAt : null };
	} catch { return { force: true, issuedAt: null }; }
}

// 🔐 임시비번 강제화면의 새 비번 설정 — 사이드카가 관리자 권한으로 설정하고 플래그까지 정리한다(2026-08-27, /api/tpt/auth/pw-set).
//   반환 true = 완료(기존 토큰이 무효될 수 있으니 호출부는 새 비번으로 재로그인). false = 라우트 없음/사이드카 장애/임시비번 상태 아님
//   → 호출부가 종전 경로(RC 본인 변경 + pw-force)로 폴백. 정책 위반 등 4xx 는 Error 로 던져 폼에 사유를 보인다.
export async function rcPwSet(token: string, userId: string, newPassword: string): Promise<boolean> {
	let r: Response;
	try {
		r = await fetch('/api/tpt/auth/pw-set', {
			method: 'POST', headers: { 'X-Auth-Token': token, 'X-User-Id': userId, 'Content-Type': 'application/json' },
			body: JSON.stringify({ newPassword }),
		});
	} catch { return false; }
	if (r.status === 403 || r.status === 404 || r.status === 405 || r.status === 409 || r.status >= 500) return false;
	const j = (await r.json().catch(() => ({}))) as { ok?: boolean; error?: string };
	if (!r.ok) throw new Error(j.error || `pw-set ${r.status}`);
	return j.ok === true;
}

// 멱등키 — RC 메시지 _id 형식(영숫자 17자)의 클라이언트 생성 id.
function randomMsgId(): string {
	const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
	const rnd = new Uint8Array(17);
	crypto.getRandomValues(rnd);
	let s = '';
	for (let i = 0; i < 17; i++) s += chars[rnd[i] % chars.length];
	return s;
}

// 🧵 스레드 댓글 목록 (2026-08-26) — RC `chat.getThreadMessages`(tmid 의 댓글만, ts 오름차순).
//   부모(원문)는 포함되지 않는다 — 호출부가 원문 메시지를 따로 가진다. 상한 count(기본 300).
export async function rcGetThreadMessages(token: string, userId: string, tmid: string, count = 300): Promise<RcMessage[]> {
	const url = `${API_BASE}/chat.getThreadMessages?tmid=${encodeURIComponent(tmid)}&count=${count}&sort=${encodeURIComponent('{"ts":1}')}`;
	const res = await fetch(url, { headers: authHeaders(token, userId), signal: AbortSignal.timeout(15000) });
	if (!res.ok) throw new Error(`chat.getThreadMessages failed: ${res.status}`);
	const data = await res.json();
	return (data.messages ?? []) as RcMessage[];
}

// 🧵 스레드 읽음 — RC 에 REST 가 없어 DDP 메서드 readThreads(tmid) 를 method.call 로 호출.
//   subscription.tunread 에서 tmid 를 빼고, 마지막이면 alert 도 내린다(threads/server/functions.ts readThread). 실패는 조용히.
export async function rcReadThread(token: string, userId: string, tmid: string): Promise<boolean> {
	try {
		const res = await fetch(`${API_BASE}/method.call/readThreads`, {
			method: 'POST',
			headers: authHeaders(token, userId),
			body: JSON.stringify({ message: JSON.stringify({ msg: 'method', id: String(Date.now()), method: 'readThreads', params: [tmid] }) }),
			signal: AbortSignal.timeout(10000),
		});
		return res.ok;
	} catch { return false; }
}

// 🧵 스레드 팔로우/언팔로우 — RC `chat.followMessage`/`chat.unfollowMessage`(mid=원문 id). 팔로우 = 원문 replies 에 가입 →
//   이후 댓글의 tunread·알림 대상. 언팔로우 = replies 제외 + 내 tunread 에서 제거. (2026-08-26 4차)
export async function rcFollowThread(token: string, userId: string, mid: string, follow: boolean): Promise<void> {
	const res = await fetch(`${API_BASE}/chat.${follow ? 'followMessage' : 'unfollowMessage'}`, {
		method: 'POST', headers: authHeaders(token, userId), body: JSON.stringify({ mid }), signal: AbortSignal.timeout(10000),
	});
	const data = await res.json().catch(() => ({}));
	if (!res.ok || data.success === false) throw new Error(`chat.${follow ? 'followMessage' : 'unfollowMessage'} ${res.status}: ${data.error || data.errorType || res.statusText}`);
}

// 🧵 방 전체 스레드 목록 — RC `chat.getThreadsList`(tcount 있는 원문, 마지막 댓글 시각 내림차순). 3차(2026-08-26)
export async function rcGetThreadsList(token: string, userId: string, rid: string, count = 50, text?: string): Promise<RcMessage[]> {
	// text = 원문 본문 부분일치(서버 regex, 대소문자 무시) — 스레드 검색 (2026-08-26 잔여)
	const url = `${API_BASE}/chat.getThreadsList?rid=${encodeURIComponent(rid)}&count=${count}&sort=${encodeURIComponent('{"tlm":-1}')}${text ? `&text=${encodeURIComponent(text)}` : ''}`;
	const res = await fetch(url, { headers: authHeaders(token, userId), signal: AbortSignal.timeout(15000) });
	if (!res.ok) throw new Error(`chat.getThreadsList failed: ${res.status}`);
	const data = await res.json();
	return (data.threads ?? []) as RcMessage[];
}

// 메시지 1건 조회 — 스레드 원문이 불러온 대화 범위 밖(옛 메시지)일 때 패널 헤더용
export async function rcGetMessage(token: string, userId: string, msgId: string): Promise<RcMessage | null> {
	const res = await fetch(`${API_BASE}/chat.getMessage?msgId=${encodeURIComponent(msgId)}`, { headers: authHeaders(token, userId), signal: AbortSignal.timeout(15000) });
	if (!res.ok) return null;
	const data = await res.json().catch(() => null);
	return (data?.message as RcMessage) ?? null;
}

// 전송 멱등화 (2026-07-14 아키텍처 점검 ②) — 클라 생성 _id 로 보내고, 네트워크 순단·타임아웃·5xx 는
//   같은 _id 로 최대 3회 재시도. 이전 시도가 실제로는 저장된 경우(응답만 유실) 서버가 중복 _id 를
//   거부하므로 중복 게시가 불가능하고, 그 오류는 성공으로 간주해 원본을 조회해 돌려준다.
//   (at-least-once 재시도 + 멱등 = 사실상 exactly-once. 4xx 정책 오류는 재시도 없이 즉시 실패.)
export async function rcSendMessage(
	token: string,
	userId: string,
	roomId: string,
	text: string,
	opts?: { attachments?: Array<Record<string, unknown>>; file?: Record<string, unknown>; tmid?: string; tshow?: boolean },
): Promise<RcMessage> {
	const _id = randomMsgId();
	const messagePayload: Record<string, unknown> = { _id, rid: roomId, msg: text };
	if (opts?.attachments && opts.attachments.length > 0) messagePayload.attachments = opts.attachments;
	if (opts?.file) messagePayload.file = opts.file;
	// 🧵 스레드 댓글 — tmid 가 있을 때만 tshow 를 실어 보낸다(서버는 tshow!==true 를 지운다)
	if (opts?.tmid) { messagePayload.tmid = opts.tmid; if (opts.tshow) messagePayload.tshow = true; }
	let lastErr: Error | null = null;
	for (let attempt = 0; attempt < 3; attempt++) {
		if (attempt > 0) await new Promise((r) => setTimeout(r, attempt === 1 ? 600 : 2000));
		try {
			const res = await fetch(`${API_BASE}/chat.sendMessage`, {
				method: 'POST',
				headers: authHeaders(token, userId),
				body: JSON.stringify({ message: messagePayload }),
				signal: AbortSignal.timeout(15000),
			});
			const data = await res.json().catch(() => ({}));
			if (res.ok && data.success !== false) return data.message;
			const reason = String(data.error || data.errorType || res.statusText || '');
			if (/already|duplicate|exist/i.test(reason)) {
				// 재시도 중복 — 첫 시도가 저장돼 있던 것. 원본 조회해 성공 반환(실패해도 최소 형태 반환).
				const got = await fetch(`${API_BASE}/chat.getMessage?msgId=${encodeURIComponent(_id)}`, { headers: authHeaders(token, userId) })
					.then((r) => r.json()).catch(() => null);
				if (got?.message) return got.message as RcMessage;
				return { _id, rid: roomId, msg: text, ts: new Date().toISOString(), u: { _id: userId, username: '' } } as RcMessage;
			}
			if (res.status >= 500 || res.status === 429) { lastErr = new Error(`sendMessage ${res.status}: ${reason}`); continue; }
			throw new Error(`sendMessage ${res.status}: ${reason}`);
		} catch (e) {
			const err = e as Error;
			// 네트워크 순단/타임아웃만 재시도 — 위에서 throw 한 4xx 정책 오류는 그대로 전파
			if (err.name === 'AbortError' || err.name === 'TimeoutError' || /failed to fetch|networkerror|load failed/i.test(err.message)) {
				lastErr = err;
				continue;
			}
			throw err;
		}
	}
	throw lastErr || new Error('sendMessage: 재시도 소진');
}

// 방 멤버들의 마지막 읽은 시각(ls) — 메시지 ts > ls 이면 그 멤버 안 읽음.
//   _id/isBot 동봉 — 카톡식 안읽음 카운트에서 발신자 본인(_id)·봇은 제외하기 위함.
export interface RoomReadMember { _id?: string; username: string; ls: string | null; isBot?: boolean }
export async function rcRoomReads(roomId: string): Promise<RoomReadMember[]> {
	try {
		const r = await fetch(`/api/tpt/reads?roomId=${encodeURIComponent(roomId)}`);
		if (!r.ok) return [];
		const j = await r.json();
		return (j.members || []) as RoomReadMember[];
	} catch { return []; }
}

// 메시지 수정 — 본인 메시지만 (RC 권한 검사 자동).
export async function rcUpdateMessage(
	token: string,
	userId: string,
	roomId: string,
	msgId: string,
	text: string,
): Promise<RcMessage> {
	const res = await fetch(`${API_BASE}/chat.update`, {
		method: 'POST',
		headers: authHeaders(token, userId),
		body: JSON.stringify({ roomId, msgId, text }),
	});
	const data = await res.json().catch(() => ({}));
	if (!res.ok || data.success === false) {
		throw new Error(`chat.update ${res.status}: ${data.error || data.errorType || res.statusText}`);
	}
	return data.message;
}

// 이모지 반응 토글 — emoji 는 ':smile:' 형식. shouldReact 생략 시 토글.
export async function rcReactMessage(
	token: string,
	userId: string,
	msgId: string,
	emoji: string,
	shouldReact?: boolean,
): Promise<void> {
	const body: Record<string, unknown> = { messageId: msgId, emoji };
	if (typeof shouldReact === 'boolean') body.shouldReact = shouldReact;
	const res = await fetch(`${API_BASE}/chat.react`, {
		method: 'POST',
		headers: authHeaders(token, userId),
		body: JSON.stringify(body),
	});
	if (!res.ok) {
		const data = await res.json().catch(() => ({}));
		throw new Error(`chat.react ${res.status}: ${data.error || res.statusText}`);
	}
}

// G/W whois — username/email/empNo 로 tpt_gw_user lookup. 메모리 캐시 (서버 5min + 클라 세션).
export interface GwWhois {
	found: boolean;
	source?: 'gw' | 'rc' | null;
	name?: string | null;
	/** 🌐 제2 표시 언어 이름 — 개인 설정에 등록한 사람에게만 온다(미등록이면 null/없음). 1순위와 같으면 담기지 않는다. */
	name2?: string | null;
	email?: string | null;
	empNo?: string | null;
	companyId?: string | null;    // 🪪 회사 코드(COM0=탑엔지니어링·COM1=파워로직스…) — 인사카드 등 회사별 연동 분기 (2026-09-16)
	deptCode?: string | null;
	deptName?: string | null;        // 기본부서(BASIC)
	parentDeptName?: string | null;  // 상위부서
	birthday?: string | null;        // 생일
	extension?: string | null;       // 내선번호
	title?: string | null;
	/** 번역 전 원본 직책(한국어) — 멘션 호칭 판정용. 구 사이드카는 안 보낸다 */
	titleRaw?: string | null;
	/** 번역 전 원본 직급(한국어) — 멘션 호칭은 언어와 무관하게 이 값("프로님") */
	positionRaw?: string | null;
	position?: string | null;
	phone?: string | null;
	company?: string | null;
	jobDeptName?: string | null;  // 겸직 부서명(첫 번째 — 호환)
	jobDeptNames?: string | null; // 겸직 부서명 전체(쉼표 구분)
	jobTitle?: string | null;     // 겸직 직책
	rcUsername?: string | null;
	nameAliases?: string[] | null; // 이름 표기 전체(한/영/중 + 오버레이 5언어) — 멘션 판별용 (2026-08-22)
}

// 클라 세션 캐시 — 🔧 2026-08-25 TTL 10분 추가: 종전엔 세션 내내 유지돼 조직연동 적용(겸직 해제·부서 이동) 뒤에도
//   새로고침 전까지 사용자 카드에 옛 겸직부서가 남았다(최연재). 서버 whoisCache 는 적용 시 즉시 비우므로 최대 10분 안에 반영.
const WHOIS_CLIENT_TTL_MS = 10 * 60_000;
const whoisClientCache = new Map<string, { v: GwWhois; ts: number }>();
const whoisCacheGet = (key: string): GwWhois | undefined => {
	const e = whoisClientCache.get(key);
	if (!e) return undefined;
	if (Date.now() - e.ts > WHOIS_CLIENT_TTL_MS) { whoisClientCache.delete(key); return undefined; }
	return e.v;
};
const whoisCacheSet = (key: string, v: GwWhois): void => { whoisClientCache.set(key, { v, ts: Date.now() }); };

export async function rcGwWhois(opts: { username?: string; email?: string; empNo?: string }): Promise<GwWhois> {
	const lang = String(i18next.language || 'ko');
	const lang2 = getNameLang2();   // 🌐 제2 이름 표기 언어 (2026-09-15)
	const key = `${JSON.stringify(opts)}|${lang}|${lang2}`; // 선호언어 변경 시 다른 표시명을 받도록 캐시키에 포함
	const cached = whoisCacheGet(key);
	if (cached) return cached;
	const qs = new URLSearchParams();
	if (opts.username) qs.set('username', opts.username);
	if (opts.email) qs.set('email', opts.email);
	if (opts.empNo) qs.set('empNo', opts.empNo);
	qs.set('lang', lang);
	if (lang2) qs.set('lang2', lang2);
	try {
		// 인증 헤더 동봉 — 서버가 준회원(guest) 차단 가드(requireNonGuest)에 사용.
		const { authToken: _wt, userId: _wu } = useAuthStore.getState();
		const wh: Record<string, string> = _wt && _wu ? { 'X-Auth-Token': _wt, 'X-User-Id': _wu } : {};
		const r = await fetch(`/api/tpt/gw/whois?${qs.toString()}`, { headers: wh });
		if (!r.ok) return { found: false };
		const j = (await r.json()) as GwWhois;
		// 🔧 2026-08-18: 못 찾은 결과(found:false)는 캐시하지 않는다 — 일시적 실패(가드 401/403·서버 캐시 미스)가
		//    세션 내내 고착돼 사용자 정보가 계속 빈칸으로 남던 문제. 성공 결과만 캐시.
		if (j.found) whoisCacheSet(key, j);
		return j;
	} catch {
		return { found: false };
	}
}

// 🔥 배치 whois '없음' 짧은 캐시 (2026-09-30) — 키 → 재조회 허용 시각
const WHOIS_NEG_TTL_MS = 2 * 60_000;
const _whoisNegUntil = new Map<string, number>();
// 배치 whois — DM/멤버 목록처럼 여러 명을 한 번에. 클라 세션 캐시(whoisClientCache) 적중분은 제외하고
// 미스만 서버 배치(/gw/whois-batch)로 1요청 처리 → 사람마다 fetch 하던 N+1 제거. 결과는 입력 순서대로.
export async function rcGwWhoisBatch(list: { username?: string; email?: string; empNo?: string }[]): Promise<GwWhois[]> {
	const lang = String(i18next.language || 'ko');
	const lang2 = getNameLang2();   // 🌐 제2 이름 표기 언어 — 미등록('')이면 서버가 name2 를 담지 않는다 (2026-09-15)
	const out: GwWhois[] = new Array(list.length).fill(null).map(() => ({ found: false }));
	const misses: { idx: number; key: string; opts: { username?: string; email?: string; empNo?: string } }[] = [];
	const nowMs = Date.now();
	list.forEach((opts, idx) => {
		const key = `${JSON.stringify(opts)}|${lang}|${lang2}`;   // 캐시 키에 lang2 — 등록/해제가 즉시 반영되게
		const cached = whoisCacheGet(key);
		if (cached) out[idx] = cached;
		else if ((_whoisNegUntil.get(key) || 0) > nowMs) { /* 🔥 서버가 방금 '없음'이라 답한 사람 — 2분 동안 재조회 생략 (out 은 found:false 그대로) */ }
		else misses.push({ idx, key, opts });
	});
	if (misses.length === 0) return out;
	try {
		const { authToken: _wt, userId: _wu } = useAuthStore.getState();
		const wh: Record<string, string> = { 'Content-Type': 'application/json', ...(_wt && _wu ? { 'X-Auth-Token': _wt, 'X-User-Id': _wu } : {}) };
		const r = await fetch('/api/tpt/gw/whois-batch', { method: 'POST', headers: wh, body: JSON.stringify({ lang, lang2, items: misses.map((m) => m.opts) }) });
		if (r.ok) {
			const j = (await r.json()) as { results?: GwWhois[] };
			const results = Array.isArray(j.results) ? j.results : [];
			misses.forEach((m, i) => {
				const v = results[i] || { found: false };
				if (v.found) whoisCacheSet(m.key, v); // 못 찾은 결과는 캐시 금지(단건과 동일 정책, 2026-08-18)
				// 🔥 단, 서버가 **정상 응답(200)으로** '없음'이라 한 사람(봇·외부·조직도 미등록)은 2분만 재조회 생략 (2026-09-30 과열 점검) —
				//   읽음 폴링(1.5s)이 매 틱 whois 를 부르면서 이런 멤버마다 POST 가 분당 ~40회 나갔다. 네트워크 실패는 여기 안 오니
				//   08-18 의 '일시 실패 고착' 문제는 재발하지 않고, 2분이면 새로 등록된 사람도 곧 보인다.
				else { _whoisNegUntil.set(m.key, Date.now() + WHOIS_NEG_TTL_MS); if (_whoisNegUntil.size > 2000) { const k0 = _whoisNegUntil.keys().next().value; if (k0) _whoisNegUntil.delete(k0); } }
				out[m.idx] = v;
			});
		}
	} catch { /* 실패 시 미스 항목은 {found:false} 유지 */ }
	return out;
}

// 이름 + 직책(직급) — 예: "홍길동 팀장(책임)" / 직책만: "홍길동 팀장" / 직급만: "홍길동(책임)".
export function formatNameRole(w: { name?: string | null; title?: string | null; position?: string | null } | null | undefined): string {
	if (!w) return '';
	const n = (w.name || '').trim();
	const title = (w.title || '').trim();
	const pos = (w.position || '').trim();
	if (!n) return [title, pos && pos !== title ? `(${pos})` : ''].filter(Boolean).join('');
	// 이미 RC name 에 직책이 들어있으면(예: "홍길동 팀장") 중복 방지
	const nameHasTitle = title && n.endsWith(title);
	const rolePart = title && !nameHasTitle ? ` ${title}` : '';
	const posPart = pos && pos !== title ? `(${pos})` : '';
	return `${n}${rolePart}${posPart}`;
}

// 헤더 한 줄 포맷: 부서 · 직책 · 이름. 누락 항목은 자동 생략.
export function formatGwIdentity(w: GwWhois): string | null {
	if (!w.found) return null;
	const parts: string[] = [];
	if (w.deptName) parts.push(w.deptName);
	if (w.title) parts.push(w.title);
	if (w.position && w.position !== w.title) parts.push(w.position);
	if (w.name) parts.push(w.name.trim());
	return parts.length > 0 ? parts.join(' · ') : null;
}

// 파일 업로드 — RC 버전 호환:
//   - RC 8.0+ : rooms.media + rooms.mediaConfirm (2-step). 8.x 에서 rooms.upload 제거됨.
//   - RC 6.x  : rooms.upload (single-step). rooms.media 자체 없음 → 405.
// 모듈 스코프 캐시 (`uploadStrategy`) — 첫 호출에서 한 번 감지하면 이후 정답 endpoint 직행.
//   'media'  = 8.x 식 2-step
//   'upload' = 6.x 식 single-step
//   null     = 미감지 (첫 호출은 upload 시도 → 405/404 면 media 전환 + 캐시 — 2026-09-28 순서 뒤집음, rcRoomUploadImpl 주석)
let uploadStrategy: 'media' | 'upload' | null = null;

// 업로드 진행률 콜백 — 대용량 파일에서 "멈춘 것처럼" 보이던 것 해소 (fetch 는 업로드 progress 미지원 → XHR, 2026-07-07)
export type UploadProgressFn = (loaded: number, total: number) => void;
const UPLOAD_STALL_MS = 20_000;   // 본문 송신 중 진행률 무변화 허용 한도 — 느린 망도 진행 이벤트는 수백 ms 간격으로 온다

/** 사용자 취소로 업로드를 중단했을 때 — 호출부가 '실패'로 오인해 오류를 띄우지 않도록 구분한다 (DownloadCanceled 와 동일 컨셉, 2026-09-01). */
export class UploadCanceled extends Error {
	constructor() { super('canceled'); this.name = 'UploadCanceled'; }
}

function xhrUpload(url: string, headers: Record<string, string>, fd: FormData, onProgress?: UploadProgressFn, signal?: AbortSignal): Promise<{ status: number; json: Record<string, unknown> }> {
	return new Promise((resolve, reject) => {
		const xhr = new XMLHttpRequest();
		xhr.open('POST', url);
		for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
		// ⏱ 전송 정지 감시 (2026-09-28 iOS 우편함 26) — WebKit 은 서버가 본문을 다 받기 전 응답·중단하면 에러 없이 송신에서
		//   멈춘다(실측: 128KB 에서 진행률 정지, 사용자가 취소할 때까지 그대로). 본문 송신 중 진행이 UPLOAD_STALL_MS 동안 없으면
		//   끊고 'upload stalled' 로 실패시켜 호출부 재시도(isTruncatedUpload)가 받게 한다. 본문을 다 보낸 뒤(서버 처리)는 감시하지 않는다.
		let stallTimer: ReturnType<typeof setTimeout> | undefined;
		let stalled = false;
		const armStall = () => { clearTimeout(stallTimer); stallTimer = setTimeout(() => { stalled = true; xhr.abort(); }, UPLOAD_STALL_MS); };
		xhr.upload.onprogress = (e) => { armStall(); if (onProgress && e.lengthComputable) onProgress(e.loaded, e.total); };
		xhr.upload.onload = () => clearTimeout(stallTimer);
		xhr.addEventListener('loadend', () => clearTimeout(stallTimer));
		// ⏹ 업로드 중단 (2026-09-01 사용자 "대용량 업로드 중단 기능 없어졌음") — signal.abort() → xhr.abort()
		if (signal) {
			if (signal.aborted) { reject(new UploadCanceled()); return; }
			const onAbort = () => xhr.abort();
			signal.addEventListener('abort', onAbort, { once: true });
			xhr.onloadend = () => signal.removeEventListener('abort', onAbort);
		}
		xhr.onload = () => {
			let json: Record<string, unknown> = {};
			try { json = JSON.parse(xhr.responseText || '{}'); } catch { /* 413 등 HTML 응답 */ }
			resolve({ status: xhr.status, json });
		};
		// 메시지는 기존 fetch 실패 패턴('fetch failed')과 맞춤 — rcRoomUpload 의 networkLike 폴백 로직 재사용
		xhr.onerror = () => reject(new Error('fetch failed (upload network error)'));
		xhr.onabort = () => reject(signal?.aborted ? new UploadCanceled() : new Error(stalled ? 'upload stalled' : 'upload aborted'));
		armStall();
		xhr.send(fd);
	});
}

/** 전송이 중간에 끊긴 실패인가 — nginx 400·본문 없음(선언 길이보다 짧게 끝남) 또는 네트워크 오류. 서버가 받은 게 없어 재시도가 안전하다(중복 게시 없음). */
function isTruncatedUpload(e: unknown): boolean {
	const err = e as Error & { status?: number; truncated?: boolean };
	if (err.truncated) return true;   // 400·RC 오류 본문 없음 — uploadViaRoomsUploadOnce 가 표시(문구는 언어별이라 텍스트 매칭 금지)
	return !err.status && /fetch failed|networkerror|load failed|upload aborted|upload stalled/i.test(err.message || '');
}

async function uploadViaRoomsUpload(token: string, userId: string, roomId: string, file: File | Blob, opts?: { description?: string; msg?: string; tmid?: string; onProgress?: UploadProgressFn; signal?: AbortSignal }): Promise<{ fileId: string; url: string; messageId?: string }> {
	// 🔁 끊긴 전송 1회 자동 재시도 (2026-09-23 맥 우편함 (16) "사진 전송이 11%·18% 에서 멈춤, 취소 후 재전송하면 성공") — 0.2.719 의 전량 읽기 뒤에도
	//   iPhone 에서 400(본문 0) 이 하루 4건 남았다(전송 도중 스트림 끊김 — WKWebView 백그라운드 전환 의심). 사용자가 손으로 하던 '취소→재전송' 을 한 번 대신한다.
	// 🧠 429 = 서버가 다른 큰 파일(100MB+)을 받는 중 (2026-10-27 nginx 동시 2개 제한 — RC 메모리 초과 재발 방지).
	//   실패로 끝내지 않고 4~7초 쉬었다 다시 보낸다(최대 약 8분). 사용자가 취소하면 바로 멈춘다.
	for (let wait = 0; ; wait++) {
		try { return await uploadViaRoomsUploadOnceRetry(token, userId, roomId, file, opts); }
		catch (e) {
			if (e instanceof UploadCanceled || opts?.signal?.aborted) throw e;
			if ((e as { status?: number }).status !== 429 || wait >= 90) throw e;
			opts?.onProgress?.(0, file.size || 1);
			await new Promise((r) => setTimeout(r, 4000 + Math.floor(Math.random() * 3000)));
			if (opts?.signal?.aborted) throw new UploadCanceled();
		}
	}
}

async function uploadViaRoomsUploadOnceRetry(token: string, userId: string, roomId: string, file: File | Blob, opts?: { description?: string; msg?: string; tmid?: string; onProgress?: UploadProgressFn; signal?: AbortSignal }): Promise<{ fileId: string; url: string; messageId?: string }> {
	try { return await uploadViaRoomsUploadOnce(token, userId, roomId, file, opts); }
	catch (e) {
		if (e instanceof UploadCanceled || opts?.signal?.aborted || !isTruncatedUpload(e)) throw e;
		console.info('[rcRoomUpload] 전송 끊김 — 1초 뒤 재시도', (e as Error).message);
		await new Promise((r) => setTimeout(r, 1000));
		return uploadViaRoomsUploadOnce(token, userId, roomId, file, opts);
	}
}

async function uploadViaRoomsUploadOnce(token: string, userId: string, roomId: string, file: File | Blob, opts?: { description?: string; msg?: string; tmid?: string; onProgress?: UploadProgressFn; signal?: AbortSignal }): Promise<{ fileId: string; url: string; messageId?: string }> {
	const fd = new FormData();
	fd.append('file', file, (file as File).name || 'upload');
	if (opts?.msg) fd.append('msg', opts.msg);
	if (opts?.description) fd.append('description', opts.description);
	if (opts?.tmid) fd.append('tmid', opts.tmid);
	const { status, json } = await xhrUpload(`${API_BASE}/rooms.upload/${encodeURIComponent(roomId)}`,
		{ 'X-Auth-Token': token, 'X-User-Id': userId }, fd, opts?.onProgress, opts?.signal);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const j = json as any;
	if (status < 200 || status >= 300 || j.success === false) {
		// 400 인데 RC 오류 본문이 없다 = nginx 가 본문을 다 못 받고 끊은 것(길이 불일치) — 큰 파일 스트리밍 경로에서 남는 케이스
		const truncated = status === 400 && !j.error && !j.errorType;
		const friendly = status === 413 ? i18next.t('dialog.rcFileTooLarge')
			: truncated ? i18next.t('dialog.uploadReadFail', { defaultValue: '파일을 읽지 못해 보내지 못했습니다. 사진이 기기에 내려받아졌는지 확인한 뒤 다시 시도해 주세요.' })
			: String(j.error || j.errorType || `HTTP ${status}`);
		const err = new Error(`rooms.upload ${status}: ${friendly}`) as Error & { status?: number; truncated?: boolean };
		err.status = status;
		err.truncated = truncated;
		throw err;
	}
	const msg = j.message || {};
	const fileMeta = msg.file || (Array.isArray(msg.attachments) ? msg.attachments[0]?.file : undefined) || {};
	const fileId = fileMeta._id || msg.file?._id || '';
	const url = fileMeta.url || (Array.isArray(msg.attachments) ? msg.attachments[0]?.title_link : '') || '';
	return { fileId, url, messageId: msg._id };
}

async function uploadViaRoomsMedia(token: string, userId: string, roomId: string, file: File | Blob, opts?: { description?: string; msg?: string; tmid?: string; onProgress?: UploadProgressFn; signal?: AbortSignal }): Promise<{ fileId: string; url: string; messageId?: string }> {
	const fd = new FormData();
	fd.append('file', file, (file as File).name || 'upload');
	if (opts?.description) fd.append('description', opts.description);
	const { status: s1, json: json1 } = await xhrUpload(`${API_BASE}/rooms.media/${encodeURIComponent(roomId)}`,
		{ 'X-Auth-Token': token, 'X-User-Id': userId }, fd, opts?.onProgress, opts?.signal);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const j1 = json1 as any;
	if (s1 < 200 || s1 >= 300 || j1.success === false) {
		const friendly = s1 === 413 ? i18next.t('dialog.rcFileTooLarge') : String(j1.error || j1.errorType || `HTTP ${s1}`);
		const err = new Error(`rooms.media ${s1}: ${friendly}`);
		(err as Error & { status?: number }).status = s1;
		throw err;
	}
	const fileId = j1.file?._id;
	const url = j1.file?.url;
	if (!fileId) throw new Error('rooms.media: file._id 누락');
	// ⏹ 전송 완료 직후(confirm 직전) 취소 — 메시지 게시 전이면 여기서 멈춘다 (업로드 파일은 미확정 상태로 서버가 정리)
	if (opts?.signal?.aborted) throw new UploadCanceled();
	const body: Record<string, unknown> = {};
	if (opts?.msg) body.msg = opts.msg;
	if (opts?.description) body.description = opts.description;
	if (opts?.tmid) body.tmid = opts.tmid;
	const r2 = await fetch(`${API_BASE}/rooms.mediaConfirm/${encodeURIComponent(roomId)}/${encodeURIComponent(fileId)}`, {
		method: 'POST',
		headers: authHeaders(token, userId),
		body: JSON.stringify(body),
	});
	const j2 = await r2.json().catch(() => ({}));
	if (!r2.ok || j2.success === false) {
		throw new Error(`rooms.mediaConfirm ${r2.status}: ${j2.error || j2.errorType || r2.statusText}`);
	}
	return { fileId, url, messageId: j2.message?._id };
}

// 📱 업로드 중 백그라운드 유예 (2026-09-28 iOS 우편함 19) — iOS 는 앱이 백그라운드로 가면 WKWebView 네트워크를
//   수 초 안에 멈춰 전송 중이던 POST 가 본문을 다 못 보낸 채 끝난다(nginx 400·본문 0). 셸이 uploadBegin/uploadEnd 를
//   노출하면 업로드 구간만 유예를 요청한다. 동시 업로드(묶음 전송)는 여기서 합쳐 셸에는 begin·end 를 한 번씩만 보낸다.
let uploadsInFlight = 0;
function uploadGrace(delta: 1 | -1): void {
	uploadsInFlight = Math.max(0, uploadsInFlight + delta);
	const b = typeof window !== 'undefined' ? window.TPTAndroid : undefined;
	try {
		if (delta === 1 && uploadsInFlight === 1) b?.uploadBegin?.();
		else if (delta === -1 && uploadsInFlight === 0) b?.uploadEnd?.();
	} catch { /* 셸 브릿지 실패는 업로드를 막지 않는다 */ }
}

export async function rcRoomUpload(
	token: string,
	userId: string,
	roomId: string,
	file: File | Blob,
	opts?: { description?: string; msg?: string; tmid?: string; onProgress?: UploadProgressFn; signal?: AbortSignal },
): Promise<{ fileId: string; url: string; messageId?: string }> {
	uploadGrace(1);
	try { return await rcRoomUploadImpl(token, userId, roomId, file, opts); } finally { uploadGrace(-1); }
}

async function rcRoomUploadImpl(
	token: string,
	userId: string,
	roomId: string,
	file: File | Blob,
	opts?: { description?: string; msg?: string; tmid?: string; onProgress?: UploadProgressFn; signal?: AbortSignal },
): Promise<{ fileId: string; url: string; messageId?: string }> {
	// 📤 전송 전 길이 확정 (2026-09-22 폰 갤러리 업로드 400·본문 0) — 선택기가 준 File 을 끝까지 못 읽으면 HTTP/2 스트림이
	//   짧게 끝나 nginx 가 400 을 낸다. 먼저 메모리로 읽어 확정된 Blob 으로 보내고, 못 읽으면 전송 전에 사람이 읽을 문구로.
	try { file = await stabilizeUploadFile(file as File); } catch (e) {
		if (e instanceof UploadReadError) {
			const err = new Error(i18next.t('dialog.uploadReadFail', { defaultValue: '파일을 읽지 못해 보내지 못했습니다. 사진이 기기에 내려받아졌는지 확인한 뒤 다시 시도해 주세요.' })) as Error & { status?: number };
			err.name = 'UploadReadError';
			throw err;
		}
		throw e;
	}
	// 캐시 hit — 정답 endpoint 직행
	if (uploadStrategy === 'media') return uploadViaRoomsMedia(token, userId, roomId, file, opts);
	if (uploadStrategy === 'upload') return uploadViaRoomsUpload(token, userId, roomId, file, opts);
	// 미감지 — rooms.upload 먼저 (2026-09-28 iOS 우편함 26 뒤집음). 운영 RC 는 6.5 라 rooms.media 가 없다(7일간 71건 전부 405·성공 0).
	//   예전 순서(media 먼저 → 405 면 upload)는 모든 첫 업로드가 파일을 두 번 보냈고, iOS WebKit 은 본문 도중 온 405 를 올리지 않고
	//   송신에서 멈춰(128KB 정지) 폴백 자체를 못 탔다 — "사진 1장부터 실패". RC 8.x(rooms.upload 제거)면 404/405 → media 로 바꾸고 고정한다.
	try {
		const out = await uploadViaRoomsUpload(token, userId, roomId, file, opts);
		uploadStrategy = 'upload';
		return out;
	} catch (e) {
		if (e instanceof UploadCanceled) throw e;   // ⏹ 사용자 중단 — 폴백 재시도 금지
		const status = (e as Error & { status?: number }).status;
		if (status === 404 || status === 405) {
			uploadStrategy = 'media';
			console.info('[rcRoomUpload] rooms.upload 미지원 — rooms.media 로 전환', { status });
			return uploadViaRoomsMedia(token, userId, roomId, file, opts);
		}
		throw e;
	}
}

// RC 액션 실패 — errorType(예: 'error-action-not-allowed')을 보존해 호출부가 사용자 문구로 바꿀 수 있게 한다.
//   message 는 기존 형식을 유지(다른 호출부의 humanizeError/로그 호환).
export class RcActionError extends Error {
	status: number;
	code: string;
	constructor(status: number, code: string, message: string) {
		super(message);
		this.name = 'RcActionError';
		this.status = status;
		this.code = code;
	}
}

/** RC 에러코드 추출 — RcActionError 면 code, 아니면 메시지의 `[error-...]` 패턴에서 (없으면 ''). */
export function rcErrorCode(e: unknown): string {
	if (e instanceof RcActionError) return e.code;
	const m = /\[(error-[a-z0-9-]+)\]/i.exec((e as Error)?.message || '');
	return m ? m[1] : '';
}

// 🗑 chat.delete 가 '이미 없는 메시지'를 지목했는지 — RC 는 이 실패를 errorType 없이
//   `No message found with the id of "..."` 문자열 하나로만 내려준다 (2026-08-24 실사고:
//   삭제가 이미 성공했는데 말풍선이 남아 재클릭 → 이 400 에 "다시 시도" 문구가 떠 반복 유발).
export function isMessageGoneError(e: unknown): boolean {
	if (rcErrorCode(e) === 'error-invalid-message') return true;
	return /No message found with the id/i.test((e as Error)?.message || '');
}

// M27 — 본인 메시지 삭제 (보내기 취소). RC chat.delete 는 본인 + 권한 검사 자동.
export async function rcDeleteMessage(
	token: string,
	userId: string,
	roomId: string,
	msgId: string,
): Promise<void> {
	const res = await fetch(`${API_BASE}/chat.delete`, {
		method: 'POST',
		headers: authHeaders(token, userId),
		body: JSON.stringify({ msgId, roomId, asUser: true }),
	});
	if (!res.ok) {
		const data = await res.json().catch(() => ({})) as { error?: string; errorType?: string };
		const raw = data.error || res.statusText;
		// RC 는 Meteor.Error 를 errorType 필드 + error 문자열("Not allowed [error-action-not-allowed]") 로 함께 낸다.
		const code = data.errorType || (/\[(error-[a-z0-9-]+)\]/i.exec(raw)?.[1] ?? '');
		throw new RcActionError(res.status, code, `chat.delete ${res.status}: ${raw}`);
	}
}

// 새 토픽(공개 채널) 생성 — channels.create. 생성된 채널 반환.
export async function rcCreateChannel(token: string, userId: string, name: string, isPrivate = false): Promise<RcChannel> {
	const ep = isPrivate ? 'groups.create' : 'channels.create';
	const res = await fetch(`${API_BASE}/${ep}`, {
		method: 'POST', headers: authHeaders(token, userId),
		body: JSON.stringify({ name }),
	});
	const d = await res.json().catch(() => ({}));
	if (!res.ok || d.success === false) throw new Error(d.error || d.errorType || `create ${res.status}`);
	return (d.channel || d.group) as RcChannel;
}

export interface RcFile {
	_id: string;
	name: string;
	type?: string;       // MIME like 'image/png', 'application/pdf'
	size?: number;
	uploadedAt?: string;
	user?: { _id: string; username: string; name?: string };
	uploader?: { _id: string; username: string; name?: string };
	url?: string;
	path?: string;
	rid?: string;
}

export async function rcRoomFiles(
	token: string,
	userId: string,
	room: RcChannel,
	count = 50,
): Promise<RcFile[]> {
	const endpoint = room.t === 'c' ? 'channels.files' : room.t === 'd' ? 'im.files' : room.t === 'p' ? 'groups.files' : null;
	if (!endpoint) return [];
	const url = new URL(`${API_BASE}/${endpoint}`, window.location.origin);
	url.searchParams.set('roomId', room._id);
	url.searchParams.set('count', String(count));
	const res = await fetch(url.pathname + url.search, { headers: authHeaders(token, userId) });
	if (!res.ok) return [];
	const data = await res.json();
	return data.files ?? [];
}

export interface RcSpotlight {
	users: Array<{ _id: string; username: string; name?: string; status?: string }>;
	rooms: Array<{ _id: string; name?: string; fname?: string; t: 'c' | 'p' | 'd'; topic?: string; usersCount?: number }>;
}

// 가입 채널 목록 (#채널 자동완성용). name 만 가벼운 조회.
let joinedChannelsCache: Array<{ _id: string; name: string }> | null = null;
export async function rcJoinedChannels(token: string, userId: string): Promise<Array<{ _id: string; name: string }>> {
	if (joinedChannelsCache) return joinedChannelsCache;
	try {
		const url = new URL(`${API_BASE}/channels.list.joined`, window.location.origin);
		url.searchParams.set('count', '0');
		const res = await fetch(url.pathname + url.search, { headers: authHeaders(token, userId) });
		if (!res.ok) return [];
		const data = await res.json();
		joinedChannelsCache = (data.channels ?? [])
			.filter((c: { name?: string }) => c.name)
			.map((c: { _id: string; name: string }) => ({ _id: c._id, name: c.name }));
		return joinedChannelsCache as Array<{ _id: string; name: string }>;
	} catch { return []; }
}

export async function rcSpotlight(
	token: string,
	userId: string,
	query: string,
): Promise<RcSpotlight> {
	const url = new URL(`${API_BASE}/spotlight`, window.location.origin);
	url.searchParams.set('query', query);
	const res = await fetch(url.pathname + url.search, { headers: authHeaders(token, userId) });
	if (!res.ok) return { users: [], rooms: [] };
	const data = await res.json();
	let users = (data.users ?? []) as RcSpotlight['users'];
	// 🚫 표시 제외 부서(관리자 설정) 소속 인원은 이름 검색에서도 제외 (2026-08-25) — 조직도 응답의 hidden.users 로 거름.
	//   모든 이름 검색(방 만들기·멤버 초대·전역 검색·조직도 탭)이 이 함수를 지나므로 여기 한 곳. 조직도 미가용(준회원 401 등)이면 필터 없이.
	//   동적 import — orgTree 는 authStore/i18n 을 끌어와 정적 import 시 순환 위험.
	try {
		const { loadOrgTreeWithMeta } = await import('./orgTree');
		const hidden = new Set((await loadOrgTreeWithMeta()).hidden?.users || []);
		if (hidden.size) users = users.filter((u) => !hidden.has(String((u as { username?: string }).username || '')));
	} catch { /* 조직도 없음 — 필터 생략 */ }
	return { users, rooms: data.rooms ?? [] };
}

export async function rcOpenDm(
	token: string,
	userId: string,
	otherUsername: string,
): Promise<{ rid: string }> {
	const res = await fetch(`${API_BASE}/im.create`, {
		method: 'POST',
		headers: authHeaders(token, userId),
		body: JSON.stringify({ username: otherUsername }),
	});
	if (!res.ok) throw new Error(`im.create failed: ${res.status}`);
	const data = await res.json();
	return { rid: data.room?._id ?? data.room?.rid };
}

// ============================================================
// M21.T2 — 멤버 초대 (admin only)
// ============================================================
export async function rcCreateUser(
	token: string,
	userId: string,
	payload: { email: string; name: string; username: string; password: string; sendWelcomeEmail?: boolean },
): Promise<{ ok: true; id: string } | { ok: false; error: string }> {
	const res = await fetch(`${API_BASE}/users.create`, {
		method: 'POST',
		headers: authHeaders(token, userId),
		body: JSON.stringify(payload),
	});
	const data = await res.json().catch(() => ({}));
	if (!res.ok || data.success === false) {
		return { ok: false, error: data.error || data.errorType || `${res.status}` };
	}
	return { ok: true, id: data.user?._id ?? '' };
}

// ============================================================
// M21.T5 — 부재중 (status + statusText)
// ============================================================
export type UserStatus = 'online' | 'away' | 'busy' | 'offline';

// ============================================================
// M22.T5 — 즐겨찾기 (별표) / T6 핀 / T8 내 글
// ============================================================
export async function rcStarMessage(
	token: string, userId: string, messageId: string, starred: boolean,
): Promise<boolean> {
	const ep = starred ? 'chat.starMessage' : 'chat.unStarMessage';
	const res = await fetch(`${API_BASE}/${ep}`, {
		method: 'POST', headers: authHeaders(token, userId),
		body: JSON.stringify({ messageId }),
	});
	return res.ok;
}

export async function rcStarredMessages(
	token: string, userId: string, roomId: string, count = 30,
): Promise<RcMessage[]> {
	const url = new URL(`${API_BASE}/chat.getStarredMessages`, window.location.origin);
	url.searchParams.set('roomId', roomId);
	url.searchParams.set('count', String(count));
	const res = await fetch(url.pathname + url.search, { headers: authHeaders(token, userId) });
	if (!res.ok) return [];
	const data = await res.json();
	return data.messages ?? [];
}

export async function rcPinMessage(
	token: string, userId: string, messageId: string, pinned: boolean,
): Promise<boolean> {
	const ep = pinned ? 'chat.pinMessage' : 'chat.unPinMessage';
	const res = await fetch(`${API_BASE}/${ep}`, {
		method: 'POST', headers: authHeaders(token, userId),
		body: JSON.stringify({ messageId }),
	});
	return res.ok;
}

export async function rcPinnedMessages(
	token: string, userId: string, roomId: string, count = 30,
): Promise<RcMessage[]> {
	const url = new URL(`${API_BASE}/chat.getPinnedMessages`, window.location.origin);
	url.searchParams.set('roomId', roomId);
	url.searchParams.set('count', String(count));
	const res = await fetch(url.pathname + url.search, { headers: authHeaders(token, userId) });
	if (!res.ok) return [];
	const data = await res.json();
	return data.messages ?? [];
}

// ============================================================
// M23 — 관리자 stats (admin only)
// ============================================================
export async function rcStatistics(
	token: string, userId: string,
): Promise<Record<string, unknown> | null> {
	const res = await fetch(`${API_BASE}/statistics`, { headers: authHeaders(token, userId) });
	if (!res.ok) return null;
	return res.json();
}

type RcListUser = { _id: string; username: string; name?: string; emails?: { address: string }[]; active?: boolean; roles?: string[]; lastLogin?: string };

export async function rcUsersList(
	token: string, userId: string, count = 50,
): Promise<RcListUser[]> {
	const url = new URL(`${API_BASE}/users.list`, window.location.origin);
	url.searchParams.set('count', String(count));
	const res = await fetch(url.pathname + url.search, { headers: authHeaders(token, userId) });
	if (!res.ok) return [];
	const data = await res.json();
	return data.users ?? [];
}

// users.list 는 서버 설정(API_Upper_Count_Limit, 기본 100)으로 count 가 캡된다 → 단일 호출로 전체를 못 받음.
// offset 페이지네이션으로 total 까지 누적 조회. offset 은 "실제 반환 수"만큼 전진(서버가 count 를 더 낮게 캡해도 안전).
export async function rcUsersListAll(
	token: string, userId: string, max = 10000,
): Promise<RcListUser[]> {
	const all: RcListUser[] = [];
	let offset = 0;
	let total = Infinity;
	for (let page = 0; page < 200 && all.length < total && all.length < max; page++) {
		const url = new URL(`${API_BASE}/users.list`, window.location.origin);
		url.searchParams.set('count', '100');
		url.searchParams.set('offset', String(offset));
		const res = await fetch(url.pathname + url.search, { headers: authHeaders(token, userId) });
		if (!res.ok) break;
		const data = await res.json();
		const users: RcListUser[] = data.users ?? [];
		if (users.length === 0) break;
		all.push(...users);
		total = typeof data.total === 'number' ? data.total : all.length;
		offset += users.length;
	}
	return all;
}

// 아바타 URL — sidecar 프록시(/api/tpt/avatar) 경유. 실제 업로드 사진이 있으면 그대로,
// 없으면(RC 자동 생성 이니셜) 중성 기본 이미지로 대체. RC 미인증 403 우회도 sidecar 가 처리.
// RC_ORIGIN 프리픽스: 데스크톱(Tauri)에선 절대 서버 URL, 웹에선 ''(상대경로) — <img> 라 fetch shim 이 못 잡음.
// 🔴 2026-08-12 — URL 에 앱 버전(v)을 항상 붙인다. 프록시가 ETag + max-age=60 으로 바뀌었지만,
//   그 전에 브라우저·WebView2 디스크 캐시에 박힌 옛 응답(max-age=3600)은 만료 전까지 재검증조차
//   하지 않아 "관리자 메뉴에서 사진을 바꿔도 안 바뀐다"(앱 재시작·강력 새로고침도 무효)가 됐다.
//   URL 이 배포마다 달라지면 그 잔여 캐시를 확실히 비껴간다.
//   bust — 방금 교체한 사진을 즉시 반영해야 하는 화면(관리자 패널·설정)에서 추가 무효화 값.
export const avatarUrl = (username: string, bust?: number | string): string =>
	`${RC_ORIGIN}/api/tpt/avatar/${encodeURIComponent(username)}?v=${encodeURIComponent(__APP_VERSION__)}${bust ? `&b=${encodeURIComponent(String(bust))}` : ''}`;

// 🖼 '크게 보기'용 고해상도 (2026-09-29 사용자 "크게 보기하니까 다 뭉개져서 보이네") — 서버 core/avatarFull.
//   목록 사진(avatarUrl)은 RC 가 줄인 200px 그대로, 크게 보기만 이 주소. 고해상도 얼굴 사진이라 파일 단기 핸들(t) 필수.
//   서버에 짝 맞는 사본이 없으면 일반 사진이 내려온다(화면은 그대로 동작).
export async function avatarFullSrc(username: string): Promise<string> {
	const tk = await ensureFileToken().catch(() => '');
	return `${RC_ORIGIN}/api/tpt/avatar/${encodeURIComponent(username)}?full=1&v=${encodeURIComponent(__APP_VERSION__)}${tk ? `&t=${encodeURIComponent(tk)}` : ''}`;
}
/** 사진을 바꾼 **직후** 고해상도 사본을 올린다 — 본인 또는 관리자. 실패해도 사진 변경 자체엔 영향 없음(false). */
export async function uploadAvatarFull(username: string, blob: Blob): Promise<boolean> {
	try {
		const { authToken, userId } = useAuthStore.getState();
		if (!authToken || !userId || !username) return false;
		const r = await fetch(`/api/tpt/avatar/${encodeURIComponent(username)}?full=1`, {
			method: 'POST', headers: { 'X-Auth-Token': authToken, 'X-User-Id': userId, 'Content-Type': blob.type || 'image/jpeg' }, body: blob,
		});
		return r.ok;
	} catch { return false; }
}

// 서버 자산(파일/이미지) URL 절대경로화. <img src>·<a href> 는 fetch shim 이 못 잡으므로,
// 데스크톱(Tauri)에선 상대 /file-upload·/ufs·/api 경로를 RC_ORIGIN 으로 프리픽스해 서버로 보낸다. 웹은 ''(무동작).
export const srvUrl = (u?: string | null): string => {
	if (!u) return '';
	if (/^https?:\/\//i.test(u)) return u;       // 이미 절대 URL
	return u.startsWith('/') ? `${RC_ORIGIN}${u}` : u;
};

// 📎 파일 프록시 URL (#3 PDF 미리보기 · #5 다운로드) — 데스크톱(tauri.localhost)에서 RC /file-upload 를
//   직접 fetch 하면 CORS 로 막힌다. CORS 허용된 sidecar /api/tpt/file 을 경유해 받는다.
//   rawUrl: RC 파일 경로(상대 `/file-upload/...` 또는 절대). download=true 면 attachment 강제.
//   🔒 보안(2026-06-16): URL 에 계정 토큰(rc_token)을 싣지 않는다(히스토리/로그/Referer 유출 방지).
//   대신 단기 불투명 핸들(t)만 부착 — sidecar 가 핸들→사용자토큰 매핑으로 서버측에서만 RC 호출(방 권한 유지).
//   핸들은 ensureFileToken()이 백그라운드로 발급/갱신해 모듈 캐시에 둔다(sync 렌더에서 즉시 읽기). 외부 URL 은 그대로.
let _fileHandle = '';
let _fileHandleExp = 0;
let _fileHandleInflight: Promise<string> | null = null;
export async function ensureFileToken(): Promise<string> {
	if (_fileHandle && Date.now() < _fileHandleExp - 60_000) return _fileHandle;
	if (_fileHandleInflight) return _fileHandleInflight;
	_fileHandleInflight = (async () => {
		try {
			const { authToken, userId } = useAuthStore.getState();
			if (!authToken || !userId) return '';
			const r = await fetch(`${RC_ORIGIN}/api/tpt/file-token`, { method: 'POST', headers: authHeaders(authToken, userId) });
			if (!r.ok) return '';
			const j = await r.json();
			_fileHandle = String(j.token || '');
			_fileHandleExp = Date.now() + (Number(j.ttl) || 1800) * 1000;
			return _fileHandle;
		} catch { return ''; } finally { _fileHandleInflight = null; }
	})();
	return _fileHandleInflight;
}
// 앱 부팅/로그인 직후 호출 — 첫 이미지 렌더 전에 핸들을 미리 받아둔다(깜빡임/403 방지).
export function primeFileToken(): void { void ensureFileToken(); }

// 🩹 전역 <img> 핸들 치유 (2026-09-27 사용자 "정지윤 님 미리보기 안 됨 — 토픽방") — 실측(nginx 48h): 파일 프록시 401 이 1,010건·토픽 30여 곳.
//   ① 핸들 붙은 434건은 **전부 발급 29분 이상 지난 핸들**(fresh 거절 0) — 렌더 때 박힌 URL 이 지연 로딩(loading=lazy)·재마운트로
//      나중에 요청될 때 이미 만료(TTL 30분). ② 핸들 없는 484건은 **전부 모바일 앱** — 핸들 발급 전에 그려진 이미지.
//   컴포넌트마다 재시도를 달던 방식(CachedAttachmentImg·ImageViewer)은 빠진 곳(썸네일·스레드 요약 등)이 계속 생겨 여기서 한 번에 받는다:
//   /api/tpt/file 이미지가 실패하면 새 핸들로 src 를 갈아 끼운다(요소당 최대 2회 — 2회차는 핸들 강제 재발급). share= 링크·권한 거절(403)은
//   새 핸들로도 안 열리지만 2회에서 멈추므로 폭주하지 않는다.
/** fetch 판 치유 — 미리보기(PDF·엑셀·워드·코드·ZIP)처럼 <img> 가 아닌 경로. 401 이면 핸들을 새로 받아 1회 재요청. */
export async function fetchFileFresh(url: string, init?: RequestInit): Promise<Response> {
	const r = await fetch(url, init);
	if (r.status !== 401 || !url.includes('/api/tpt/file?') || /[?&]share=/.test(url)) return r;
	_fileHandle = ''; _fileHandleExp = 0;
	const abs = /^https?:\/\//i.test(url) ? url : `${window.location.origin}${url}`;
	const fresh = await withFreshFileHandle(abs);
	return fresh === abs ? r : fetch(fresh, init);
}
let _imgHealerOn = false;
export function installFileImgHealer(): void {
	if (_imgHealerOn || typeof document === 'undefined') return;
	_imgHealerOn = true;
	document.addEventListener('error', (ev) => {
		const el = ev.target;
		if (!(el instanceof HTMLImageElement)) return;
		const src = el.currentSrc || el.src || '';
		if (!src.includes('/api/tpt/file?') || /[?&]share=/.test(src)) return;
		const tries = Number(el.dataset.tptHeal || 0);
		if (tries >= 2) return;
		el.dataset.tptHeal = String(tries + 1);
		void (async () => {
			if (tries >= 1) { _fileHandle = ''; _fileHandleExp = 0; }   // 2회차 — 클라가 유효하다고 믿는 핸들을 서버가 거절한 경우(시계 차 등)
			const fresh = await withFreshFileHandle(src);
			if (fresh && fresh !== src && el.isConnected) el.src = fresh;
		})();
	}, true);   // error 는 버블링하지 않는다 — 캡처 단계로 받는다
}

// 📱 네이티브 HTTP 스택(Android DownloadManager/HttpURLConnection)용 URL 준비 (2026-08-15 "모바일 다운로드 안 됨").
//   네이티브 스택은 인증 헤더를 못 실으므로 URL 의 단기핸들(t, TTL 30분)이 유일한 인증인데, 렌더 시점에
//   박힌 핸들이 클릭 시점엔 만료돼 401 로 죽었다 — 서버가 헤더 우선이라 웹/데탑(fetch+헤더)은 멀쩡하고
//   모바일만 실패하던 것. 내려받기 직전 새 핸들로 교체/부착한다. share= 링크는 자기완결형이라 그대로.
export async function withFreshFileHandle(abs: string): Promise<string> {
	//   📁 산출물 내려받기(/api/tpt/projects/version)도 같은 ?t= 핸들을 받는다 (2026-10-29)
	if (!(abs.includes('/api/tpt/file') || abs.includes('/api/tpt/drive/download') || abs.includes('/api/tpt/projects/version'))) return abs;
	if (/[?&]share=/.test(abs)) return abs;
	try {
		const fresh = await ensureFileToken();
		if (!fresh) return abs;
		const uo = new URL(abs);
		uo.searchParams.set('t', fresh);
		return uo.toString();
	} catch { return abs; }
}
// 현재 유효한 단기 핸들을 sync 로 읽는다(렌더 중 <img src> 에 ?t= 부착용). 없으면 백그라운드 발급 트리거 후 ''.
export function currentFileToken(): string {
	if (_fileHandle && Date.now() < _fileHandleExp) return _fileHandle;
	void ensureFileToken();
	return '';
}

export const fileProxyUrl = (rawUrl: string | undefined | null, _token?: string | null, _uid?: string | null, download = false): string => {
	if (!rawUrl) return '';
	// 🖼 data:/blob: URL 은 자기완결형(인증·프록시 불필요) — 그대로 돌려준다. 구독 카드 배너(SVG data URL)를 공통 미리보기로 열 때
	//   /api/tpt/file?path=data%3A… 로 감싸져 깨지던 것 (2026-09-12). ImageViewer 재시도(rebuildSrc)도 이 함수를 타므로 여기서 막는다.
	if (/^(data:|blob:)/i.test(rawUrl)) return rawUrl;
	// 📎 파일 공유 링크(전달된 파일) — 이미 자기완결형(share= 토큰)이라 경로는 그대로 쓰되 origin 만 수신자 것으로.
	//   전달 메시지는 RC 첨부 검증(절대 URL 만 허용) 때문에 '보낸 사람의 origin' 이 박힌 절대 URL 로 저장된다
	//   (2026-08-18). 보낸 사람이 사내 IP·데스크톱 도메인 등으로 접속했으면 받는 사람 환경에서 안 열리므로,
	//   path+query 만 취해 자기 origin(RC_ORIGIN, 웹은 상대경로)으로 되돌린다.
	if (rawUrl.includes('/api/tpt/file') && /[?&]share=/.test(rawUrl)) {
		if (/^https?:\/\//.test(rawUrl)) {
			try { const u = new URL(rawUrl); return `${RC_ORIGIN}${u.pathname}${u.search}`; } catch { return rawUrl; }
		}
		return `${RC_ORIGIN}${rawUrl}`;
	}
	let pathOnly = rawUrl;
	// 멱등 — 이미 프록시된 /api/tpt/file?path=... 이 다시 들어오면 내부 path 를 꺼내 재구성(이중 래핑 400 방지).
	if (rawUrl.includes('/api/tpt/file')) {
		try { const inner = new URL(rawUrl, RC_ORIGIN || 'http://x').searchParams.get('path'); if (inner) pathOnly = inner; } catch { /* keep */ }
	} else if (/\/api\/tpt\//.test(rawUrl)) {
		// 다른 sidecar 라우트(드라이브 download 등)는 자체 인증/스트리밍 → 그대로 사용(프록시로 감싸지 않음).
		return rawUrl;
	} else if (/^https?:\/\//i.test(rawUrl)) {
		try {
			const url = new URL(rawUrl);
			const host = typeof window !== 'undefined' ? window.location.host : '';
			// RC 가 Site_Url 기준 절대 URL(http://localhost:3000/ufs/…)로 주는 케이스 — localhost/127.* 는
			// 사용자 PC 에 존재하지 않는 주소라 그대로 쓰면 "다운로드 실패" (2026-07-13 파일탭 실증) → 내부로 간주해 프록시로 감쌈.
			const isInternal = url.host === host || RC_ORIGIN.includes(url.host) || /^(localhost|127\.(\d+\.){2}\d+)$/i.test(url.hostname);
			if (url.host && !isInternal) return rawUrl; // 진짜 외부 URL 만 그대로
			// 🔴 pathname 만 쓰면 파일명의 '#' 이후가 통째로 날아간다 (2026-08-12 사용자: "#601, #602" 가
			//   들어간 xlsx 다운로드 실패). RC 는 업로드 path 에 '#' 을 인코딩하지 않고 저장하므로
			//   URL 파서가 fragment 로 잘라낸다 → hash 를 되붙여 원래 경로를 복원한다.
			pathOnly = url.pathname + (url.hash || '');
		} catch { /* keep */ }
	}
	void ensureFileToken(); // 캐시 비었으면 백그라운드 발급 (다음 렌더에 반영)
	const qs = new URLSearchParams();
	qs.set('path', pathOnly);
	if (_fileHandle) qs.set('t', _fileHandle);
	if (download) qs.set('dl', '1');
	return `${RC_ORIGIN}/api/tpt/file?${qs.toString()}`;
};

// 📎 파일 공유 토큰 발급 (2026-07-15) — 다른 방으로 파일 '전달' 시. 발급자(=현재 사용자)가 그 파일 접근권을
//   가진 경우에만 서버가 토큰 부여. 반환 토큰으로 만든 링크는 원본 방 비멤버도 그 파일 하나만 열 수 있음.
export async function createFileShare(rawPath: string): Promise<string | null> {
	try {
		let path = rawPath;
		try { if (/^https?:\/\//.test(path)) path = new URL(path).pathname; } catch { /* keep */ }
		const { authToken, userId } = useAuthStore.getState();
		if (!authToken || !userId) return null;
		const r = await fetch(`${RC_ORIGIN}/api/tpt/file-share`, {
			method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders(authToken, userId) },
			body: JSON.stringify({ path }),
		});
		if (!r.ok) return null;
		const j = await r.json().catch(() => ({})) as { share?: string };
		return j.share || null;
	} catch { return null; }
}

// ⬇ 다운로드 진행률 — 전역 이벤트('tpt:download-progress')로 발행, App 루트의 DownloadProgress 가 표시 (2026-07-09).
//   "다운로드 눌러도 아무 화면이 안 나와서 되는 건지 모르겠다" 해소 — 모든 downloadViaBlob 호출처 공통.
let dlSeq = 0;
export interface DlProgressDetail { key: number; name: string; loaded: number; total: number; done?: boolean; error?: boolean; canceled?: boolean }
function emitDlProgress(detail: DlProgressDetail): void {
	try { window.dispatchEvent(new CustomEvent('tpt:download-progress', { detail })); } catch { /* SSR */ }
}
// ⬇ 진행 중 다운로드 취소 (2026-08-12 사용자: 잘못 눌렀을 때 중간 취소) — 진행 카드 key 별 취소 콜백.
//   DownloadProgress 의 ✕ 버튼이 호출 → 스트림 reader.cancel() 후 DownloadCanceled throw 로 조용히 중단
//   (데스크톱은 dl_end abort 로 쓰다 만 파일까지 삭제, 호출부 오류 알림 없음).
const dlCancels = new Map<number, () => void>();
export function cancelDownload(key: number): void {
	try { dlCancels.get(key)?.(); } catch { /* */ }
}
// 🚦 동시 다운로드 상한 (2026-08-18 사용자: "3개 한번에 받으면 다 안 받아지고 파일명이 뒤죽박죽") —
//   대용량 여러 개를 동시에 받으면 앱이 8MB 씩 디스크에 쓰는 동안 소켓을 안 읽어 유휴 구간이 길어지고,
//   그 사이 프록시(nginx send_timeout)가 연결을 끊어 **잘린 파일**이 완료로 저장됐다(실측: 74MB zip 을
//   16MB 만 받고 종료 → 압축을 열면 항목명이 깨져 보인다). 동시 2개로 제한해 경합 자체를 줄인다.
//   ⚠️ 반드시 저장 다이얼로그(pickSaveTarget) **뒤에** 잡는다 — 웹 showSaveFilePicker 는 사용자 제스처가
//   살아 있어야 열리므로, 픽커 앞에서 대기시키면 제스처가 소멸해 픽커가 차단된다.
const DL_MAX_CONCURRENT = 2;
let dlActive = 0;
const dlWaiters: Array<() => void> = [];
function dlAcquire(): Promise<void> {
	if (dlActive < DL_MAX_CONCURRENT) { dlActive++; return Promise.resolve(); }
	return new Promise<void>((ok) => dlWaiters.push(() => { dlActive++; ok(); }));
}
function dlRelease(): void {
	dlActive = Math.max(0, dlActive - 1);
	dlWaiters.shift()?.();
}

/** 이어받기 재시도 상한 — 이만큼 시도해도 크기가 안 맞으면 실패로 확정한다. */
const DL_RESUME_MAX = 3;
/** 끊긴 지점부터 재요청 — 206 + body 를 주면 이어받고, null 이면 이어받기 불가(=실패 확정). */
type ResumeFetch = (offset: number) => Promise<Response | null>;
const dlMb = (n: number): string => (n / 1024 / 1024).toFixed(1) + 'MB';
/**
 * 🔴 서버가 알린 크기(content-length)보다 적게 받고 스트림이 끝난 경우 (2026-08-18).
 *   fetch 스트림은 프록시가 연결을 끊어도 예외 없이 done 으로 끝나는 경우가 있어, 검증이 없으면
 *   잘린 파일이 '완료'로 저장된다. 이어받기(Range)까지 실패했을 때만 이 오류로 확정한다.
 */
export class IncompleteDownload extends Error {
	constructor(public readonly loaded: number, public readonly total: number) {
		super(i18next.t('errors.download.incomplete', {
			got: dlMb(loaded), total: dlMb(total),
			defaultValue: '다운로드가 중간에 끊겼습니다 ({{got}} / {{total}}) — 다시 시도해 주세요',
		}));
		this.name = 'IncompleteDownload';
	}
}
async function readWithProgress(r: Response, name: string, resume?: ResumeFetch): Promise<Blob> {
	const total = Number(r.headers.get('content-length') || 0);
	if (!r.body) return r.blob();   // 스트림 미지원 폴백 — 진행률 없이 기존 동작
	const key = ++dlSeq;
	emitDlProgress({ key, name, loaded: 0, total });
	// 🔁 이어받기로 교체되므로 let — 취소 콜백은 변수를 참조하니 항상 최신 reader 를 끊는다.
	let reader = r.body.getReader();
	let canceled = false;
	dlCancels.set(key, () => { canceled = true; void reader.cancel().catch(() => undefined); });
	try {
		const chunks: BlobPart[] = [];
		let loaded = 0;
		let lastEmit = 0;
		const pump = async () => {
			for (;;) {
				const { done, value } = await reader.read();
				if (canceled) throw new DownloadCanceled();   // cancel() 은 read 를 done 으로 풀므로 먼저 검사
				if (done) break;
				chunks.push(value);
				loaded += value.byteLength;
				const now = Date.now();
				if (now - lastEmit > 150) { lastEmit = now; emitDlProgress({ key, name, loaded, total }); }
			}
		};
		await pump();
		// 🔁 조기 종료 이어받기 (2026-08-18) — 끊긴 지점부터 Range 로 재개. resume 이 없거나 서버가
		//   범위를 지원하지 않으면 루프를 빠져나가 아래 완결성 검사에서 실패로 확정한다.
		for (let i = 0; resume && total > 0 && loaded < total && i < DL_RESUME_MAX; i++) {
			const rr = await resume(loaded);
			if (!rr?.body) break;
			reader = rr.body.getReader();
			await pump();
		}
		// 🔴 완결성 검증 — 없으면 잘린 파일이 '완료'로 저장된다 (IncompleteDownload 주석 참조).
		if (total > 0 && loaded < total) throw new IncompleteDownload(loaded, total);
		emitDlProgress({ key, name, loaded, total: total || loaded, done: true });
		return new Blob(chunks, { type: r.headers.get('content-type') || undefined });
	} catch (e) {
		const c = e instanceof DownloadCanceled;
		emitDlProgress({ key, name, loaded: 0, total, done: true, error: !c, canceled: c });
		throw e;
	} finally { dlCancels.delete(key); }
}

// 📥 blob 다운로드 — `<a download>` 는 cross-origin(데스크톱)에서 무시되고 navigation 으로 새 버 떠
//   다운로드가 안 됨(#5). fetch→blob→임시 anchor 로 항상 파일명대로 저장. 실패 시 throw.
/** 사용자 취소로 저장을 중단했을 때 — 호출부가 '실패'로 오인해 오류를 띄우지 않도록 구분한다. */
export class DownloadCanceled extends Error {
	constructor() { super('canceled'); this.name = 'DownloadCanceled'; }
}

/**
 * 💾 저장 위치 선택 (2026-08-12 사용자 요청: "바로 다운로드가 아니라 경로를 지정하도록").
 *   데스크톱 = Tauri 저장 다이얼로그(plugin:dialog|save, JS 패키지 없이 IPC 호출).
 *   웹 = File System Access API(showSaveFilePicker, Chrome·Edge). 미지원 브라우저는 기존 동작.
 *   ⚠️ 반드시 fetch 전에 호출 — 사용자 제스처가 살아 있어야 브라우저가 픽커를 연다.
 *   반환: 데스크톱 경로 / 웹 핸들 / null(물어보지 않음·미지원) — 취소는 DownloadCanceled throw.
 */
type SaveTarget = { kind: 'tauri'; path: string } | { kind: 'web'; handle: FileSystemFileHandle } | null;
async function pickSaveTarget(filename: string): Promise<SaveTarget> {
	const name = filename || 'download';
	// 📎 확장자 필터 (2026-08-13 사용자: "다이얼로그에도 all 로만 표시 · 확장자 자동선택되게") —
	//   필터가 없으면 OS 다이얼로그가 '모든 파일' 로 열려 확장자를 자동 부착하지 않고, 사용자가
	//   이름을 손보면 확장자 없이 저장된다. 파일의 확장자를 첫 필터로 넘겨 그것이 선택되게 한다.
	const ext = extOf(name);
	if (isTauri()) {
		// 📁 저장 위치 묻지 않기 (2026-08-31 설정) — 다이얼로그 생략 후 null 반환: runDownload 의
		//   dl_begin/save_download 가 지정 폴더(getDownloadDir, 없으면 기본 Downloads)로 바로 저장한다.
		if (!getDownloadAsk()) return null;
		try {
			const { invoke } = await import('@tauri-apps/api/core');
			const filters = ext
				? [{ name: i18next.t('preview.fileTypeExt', { ext: ext.toUpperCase(), defaultValue: '{{ext}} 파일' }), extensions: [ext] },
					{ name: i18next.t('preview.fileTypeAll', { defaultValue: '모든 파일' }), extensions: ['*'] }]
				: undefined;
			// 지정 다운로드 폴더가 있으면 다이얼로그를 그 폴더에서 연다 (2026-08-31 설정)
			const dlDir = getDownloadDir();
			const picked = await invoke<string | null>('plugin:dialog|save', { options: { defaultPath: dlDir ? `${dlDir}\\${name}` : name, ...(filters ? { filters } : {}) } });
			if (!picked) throw new DownloadCanceled();
			// 사용자가 파일명에서 확장자를 지웠어도 원래 확장자로 저장 (필터 선택과 무관하게 최종 보정)
			return { kind: 'tauri', path: ensureExt(String(picked), ext) };
		} catch (e) {
			if (e instanceof DownloadCanceled) throw e;
			console.warn('[download] 저장 다이얼로그 사용 불가 — 기본 폴더로 저장:', (e as Error).message);
			return null;   // 구버전 앱(플러그인 없음) — 기존 Downloads 폴더 경로로 폴백
		}
	}
	const picker = (window as unknown as { showSaveFilePicker?: (o: unknown) => Promise<FileSystemFileHandle> }).showSaveFilePicker;
	if (typeof picker !== 'function') return null;   // Firefox/Safari 등 — 브라우저 기본 다운로드
	try {
		// 웹(Chrome·Edge)도 types 를 주면 '모든 파일' 대신 해당 형식이 선택되고 확장자가 유지된다.
		const types = ext
			? [{ description: i18next.t('preview.fileTypeExt', { ext: ext.toUpperCase(), defaultValue: '{{ext}} 파일' }), accept: { 'application/octet-stream': [`.${ext}`] } }]
			: undefined;
		return { kind: 'web', handle: await picker.call(window, { suggestedName: name, ...(types ? { types } : {}) }) };
	} catch (e) {
		// 사용자가 취소하면 AbortError — 조용히 중단(오류 아님)
		if ((e as Error)?.name === 'AbortError') throw new DownloadCanceled();
		console.warn('[download] 저장 위치 선택 실패 — 기본 폴더로 저장:', (e as Error).message);
		return null;
	}
}

/**
 * 🔎 확장자를 어디서도 못 얻은 경우의 최후 수단 — 1바이트만 받아(Range) 서버가 주는
 *   `content-disposition` 파일명·`content-type` 으로 확장자를 확정한다. 전달된 파일 링크
 *   (`?share=`)는 URL 에 파일명이 없어 이 경로가 필요하다. 저장 다이얼로그 **전에** 호출.
 *   ⚠️ 웹은 사용자 제스처가 끊기면 픽커가 차단되므로 데스크톱(OS 다이얼로그)에서만 쓴다.
 */
async function probeServerFileName(url: string, fallback: string): Promise<string> {
	try {
		const { authToken, userId } = useAuthStore.getState();
		const headers: Record<string, string> = { Range: 'bytes=0-0' };
		if (authToken && userId && (url.includes('/api/tpt/file') || url.includes('/api/tpt/drive/download'))) {
			headers['X-Auth-Token'] = authToken; headers['X-User-Id'] = userId;
		}
		const r = await fetch(url, { headers });
		try { await r.body?.cancel(); } catch { /* */ }
		if (!r.ok && r.status !== 206) return fallback;
		return nameFromResponse(r, fallback);
	} catch { return fallback; }
}

/** 응답의 `content-disposition` 파일명(RFC 5987 포함) → 없으면 `content-type` 으로 확장자 보정. */
function nameFromResponse(r: Response, fallback: string): string {
	const cd = r.headers.get('content-disposition') || '';
	const star = /filename\*=UTF-8''([^;]+)/i.exec(cd);
	const plain = /filename="?([^";]+)"?/i.exec(cd);
	let served = '';
	if (star) { try { served = decodeURIComponent(star[1]); } catch { served = star[1]; } }
	else if (plain) served = plain[1];
	if (served && extOf(served)) return served;
	return resolveDownloadName(fallback, served || undefined, r.headers.get('content-type'));
}

export async function downloadViaBlob(url: string, filename: string, opts?: { reveal?: boolean; askPath?: boolean; mime?: string; dir?: string }): Promise<void> {
	// 💾 저장 요청은 항상 dl=1 (2026-08-05) — 없으면 서버 파일 프록시가 '미리보기'로 간주해 파수 DRM
	//   감지·해제 경로를 탄다(에이전트 지연 시 다운로드까지 매달림 + DRM 문서의 저장본이 평문이 되는 정책 위반).
	//   저장 목적인 이 함수는 원본 스트리밍(dl=1)으로 고정 — 감사 로그도 download 로 정확해진다.
	if (url.includes('/api/tpt/file') && !/[?&]dl=1(?:&|$)/.test(url)) url += (url.includes('?') ? '&' : '?') + 'dl=1';
	// 📎 확장자 확정 (2026-08-13) — 호출부가 준 이름이 비었거나 확장자가 없으면 URL·MIME 에서 복원한다.
	//   전달된 파일(`?share=`)은 URL 에 파일명이 없으므로, 데스크톱은 서버 헤더까지 물어본다(다이얼로그 전).
	filename = resolveDownloadName(filename, url, opts?.mime);
	if (!extOf(filename) && isTauri()) {
		const abs0 = url.startsWith('/') ? `${RC_ORIGIN}${url}` : url;
		filename = await probeServerFileName(abs0, filename);
	}
	// 📱 Android 앱 — blob URL 은 DownloadManager 가 못 받아 "다운로드 실패"가 나던 것(2026-07-07)
	//   → 원본 http URL 을 네이티브 브릿지로 넘겨 DownloadManager 가 직접 받게 함.
	//   🔴 DownloadManager 는 헤더를 못 실으므로 넘기기 직전 단기핸들(t)을 새것으로 교체 —
	//   렌더 시점 핸들은 만료(30분)됐을 수 있다 (2026-08-15 "모바일 다운로드 안 됨").
	//   🔴 호출은 반드시 브릿지 객체에 바인딩해서 (2026-08-16) — 언바인드로 부르면 WebView 가
	//   "Java bridge method can't be invoked on a non-injected object" 예외를 던지고, 그 뒤 웹 폴백은
	//   blob 이라 앱에서 무음 실패한다(=눌러도 아무 일 없음). shareFile·setNotifPrefs 는 이미 .call 사용.
	const androidBridge = typeof window !== 'undefined' ? window.TPTAndroid : undefined;
	if (androidBridge?.download) {
		const abs = await withFreshFileHandle(url.startsWith('/') ? `${window.location.origin}${url}` : url);
		try { androidBridge.download.call(androidBridge, abs, filename || 'download'); return; }
		catch (e) { console.warn('[download] Android 브릿지 호출 실패 — 웹 경로로 폴백:', e); }
	}
	// 💾 저장 위치 먼저 묻는다 (fetch 전 — 사용자 제스처 유효 구간). 일괄 저장은 askPath:false 로 생략.
	const saveTarget = opts?.askPath === false ? null : await pickSaveTarget(filename);
	// 🚦 여기서부터가 실제 전송 — 동시 2개로 제한 (dlAcquire 주석 참조: 픽커 뒤에서 잡아야 한다).
	await dlAcquire();
	try {
		await runDownload(url, filename, saveTarget, opts);
	} catch (e) {
		// 📁 사용자가 고른 경로를 오류에 실어 보낸다 (2026-09-29 "경로를 지정했는데 설정된 다운로드 폴더로 들어감") —
		//   묶음 [전체 저장] 은 zip 이 실패하면 파일별로 다시 받는데, 그때 이 경로의 폴더로 저장해야 사용자 의도와 맞다.
		if (saveTarget?.kind === 'tauri' && e && typeof e === 'object') (e as { savePath?: string }).savePath = saveTarget.path;
		throw e;
	} finally { dlRelease(); }
}

/**
 * 전송 본체 — 파일명·저장 위치가 확정된 뒤의 fetch → 저장. 동시 실행 수는 호출부(dlAcquire)가 제한한다.
 * downloadViaBlob 에서 분리 (2026-08-18) — 세마포어를 저장 다이얼로그 **뒤에** 두기 위한 분리로, 로직은 원문 그대로.
 */
async function runDownload(url: string, filename: string, saveTarget: SaveTarget, opts?: { reveal?: boolean; askPath?: boolean; mime?: string; dir?: string }): Promise<void> {
	// 🔒 인증은 헤더로 — sidecar 프록시(/api/tpt/file)·드라이브 다운로드(/api/tpt/drive/download)면 본인 토큰을 헤더에 실어 권한 검증·감사 로그(URL 토큰 미사용).
	let headers: Record<string, string> | undefined;
	//   📁 산출물(/api/tpt/projects/version) 포함 (2026-10-29 형 "베트남에서 큰 산출물이 받는 중인지 알 수 없다" — 진행률 표시를 위해 공용 경로로)
	if (url.includes('/api/tpt/file') || url.includes('/api/tpt/drive/download') || url.includes('/api/tpt/projects/version')) {
		const { authToken, userId } = useAuthStore.getState();
		if (authToken && userId) headers = { 'X-Auth-Token': authToken, 'X-User-Id': userId };
	}
	// 상대 경로(/api/...)는 데스크탑(tauri.localhost)에서 raw fetch 시 "Failed to fetch" → RC_ORIGIN 프리픽스(웹은 '' 라 무영향).
	const absUrl = url.startsWith('/') ? `${RC_ORIGIN}${url}` : url;
	let r: Response;
	// 🔁 이어받기(Range 재요청)는 최초에 성공한 것과 **같은 인증 방식**이어야 한다 — 아래 무헤더 폴백을
	//   탔다면 헤더를 다시 붙이면 안 되므로, 실제로 통한 방식을 기억해 둔다.
	let usedHeaders = headers;
	try {
		r = await fetch(absUrl, headers ? { headers } : undefined);
	} catch (e) {
		// 데탑 등에서 커스텀 헤더 fetch 가 "Failed to fetch" 로 죽는 케이스 폴백 (2026-07-13) —
		// ① 프록시 URL 에 단기 핸들(t=)이 있으면 헤더 없이도 인증되므로 무헤더 재시도.
		// ② 그래도 실패하면(데탑 웹뷰 fetch 자체 문제) 기본 브라우저로 열어 브라우저가 직접 받게 함.
		try {
			if (headers && /[?&]t=/.test(absUrl)) { r = await fetch(absUrl); usedHeaders = undefined; }
			else throw e;
		} catch (e2) {
			if (isTauri()) {
				const dlUrl = absUrl.includes('/api/tpt/file') && !/[?&]dl=1/.test(absUrl) ? `${absUrl}&dl=1` : absUrl;
				const { invoke } = await import('@tauri-apps/api/core');
				await invoke('open_external', { url: dlUrl });
				return; // 브라우저가 저장 — 여기서 종료(실패 아님)
			}
			throw new Error(`${(e2 as Error).message} — ${absUrl.slice(0, 80)}`);
		}
	}
	if (!r.ok) throw new Error(i18next.t('errors.download.failed', { status: r.status }));
	// 🔁 중단 지점부터 이어받기 (2026-08-18 — 74MB zip 이 16MB 만 받고 '완료'로 저장된 사례).
	//   프록시가 유휴 연결을 끊으면 fetch 스트림이 예외 없이 done 으로 끝나므로, 받은 만큼을 기준으로
	//   Range 재요청해 나머지를 이어 받는다.
	//   🔴 RC 6.5 는 /file-upload 에서 Range 를 **무시하고 200+전체**를 준다(2026-08-18 실측) → 사이드카가
	//   대신 잘라 206 을 만들어 준다. 다만 그 처리는 `resume=1` 이 붙은 요청에만 적용된다(일반 Range 까지
	//   206 을 주면 <video> seek 마다 RC 전체 재전송을 유발하므로 — files.ts 주석 참조).
	//   206 이 아니면(=범위 미지원 경로·zip 스트리밍 등) null 을 주고, 호출부는 그때 IncompleteDownload 로
	//   실패를 확정한다(잘린 파일을 남기지 않는다).
	const resumeUrl = absUrl.includes('/api/tpt/file') && !/[?&]resume=1(?:&|$)/.test(absUrl)
		? absUrl + (absUrl.includes('?') ? '&' : '?') + 'resume=1'
		: absUrl;
	const resumeFrom: ResumeFetch = async (offset) => {
		try {
			const rr = await fetch(resumeUrl, { headers: { ...(usedHeaders || {}), Range: 'bytes=' + offset + '-' } });
			if (rr.status !== 206 || !rr.body) { try { await rr.body?.cancel(); } catch { /* */ } return null; }
			return rr;
		} catch { return null; }
	};
	// 📎 확장자 2차 보정 (2026-08-13) — 아직 확장자가 없으면 서버가 준 파일명/타입으로 확정.
	//   사용자가 저장 위치를 직접 고른 경우(saveTarget)는 그 이름이 사용자 의도라 건드리지 않는다.
	if (!saveTarget && !extOf(filename)) filename = nameFromResponse(r, filename);
	// 📦 데스크톱 스트리밍 저장 (2026-08-07 #3) — 수백 MB 를 blob→단일 IPC 로 넘기면 WebView2 렌더러가
	//   죽어(검은 화면) "진행률 완료인데 파일 없음"이 되던 것. 8MB 청크로 Rust(dl_begin/chunk/end)에
	//   순차 기록 — 메모리에 전체를 안 들고, 진행률 done 도 실제 저장 완료 후 발행.
	//   ⚠️ content-length 없는(chunked) 응답도 있으므로 크기 조건 없이 데스크톱은 항상 이 경로.
	if (isTauri()) {
		const totalLen = Number(r.headers.get('content-length') || 0);
		if (r.body) {
			const name = filename || 'download';
			const { invoke } = await import('@tauri-apps/api/core');
			// 사용자가 고른 경로가 있으면 그 경로로, 없으면 지정 다운로드 폴더(2026-08-31 설정) → 기본 Downloads
			//   opts.dir = 호출부가 정한 폴더(묶음 저장 폴백 — 사용자가 고른 zip 위치의 폴더)가 지정 폴더보다 우선 (2026-09-29)
			const dlDir = opts?.dir || getDownloadDir();
			const id = saveTarget?.kind === 'tauri'
				? await invoke<number>('dl_begin_at', { path: saveTarget.path })
				: await invoke<number>('dl_begin', { filename: name, ...(dlDir ? { dir: dlDir } : {}) });
			const key = ++dlSeq;
			emitDlProgress({ key, name, loaded: 0, total: totalLen });
			// 🔁 이어받기로 교체되므로 let — 취소 콜백은 변수를 참조하니 항상 최신 reader 를 끊는다.
			let reader = r.body.getReader();
			let canceled = false;
			dlCancels.set(key, () => { canceled = true; void reader.cancel().catch(() => undefined); });
			try {
				let loaded = 0;
				let lastEmit = 0;
				let parts: Uint8Array[] = [];
				let partsLen = 0;
				const FLUSH = 8 * 1024 * 1024;
				const flush = async () => {
					if (partsLen === 0) return;
					const merged = new Uint8Array(partsLen);
					let off = 0;
					for (const c of parts) { merged.set(c, off); off += c.byteLength; }
					parts = []; partsLen = 0;
					await invoke('dl_chunk', merged.buffer, { headers: { 'x-dl-id': String(id) } });
				};
				const pump = async () => {
					for (;;) {
						const { done, value } = await reader.read();
						if (canceled) throw new DownloadCanceled();   // cancel() 은 read 를 done 으로 풀므로 먼저 검사
						if (done) break;
						parts.push(value); partsLen += value.byteLength; loaded += value.byteLength;
						if (partsLen >= FLUSH) await flush();
						const now = Date.now();
						if (now - lastEmit > 150) { lastEmit = now; emitDlProgress({ key, name, loaded, total: totalLen }); }
					}
					await flush();
				};
				await pump();
				// 🔁 조기 종료 이어받기 (2026-08-18) — 파일엔 이미 loaded 만큼 append 돼 있으므로 같은 dl id 에
				//   나머지를 이어 쓰면 된다(dl_chunk 는 append). 이어받기가 안 되면 아래 검증이 잡는다.
				for (let i = 0; totalLen > 0 && loaded < totalLen && i < DL_RESUME_MAX; i++) {
					const rr = await resumeFrom(loaded);
					if (!rr?.body) break;
					reader = rr.body.getReader();
					await pump();
				}
				// 🔴 완결성 검증 — 없으면 잘린 파일이 '완료'로 저장된다. throw 는 아래 catch 가 받아
				//   dl_end(abort:true) 로 쓰다 만 파일을 지운다.
				if (totalLen > 0 && loaded < totalLen) throw new IncompleteDownload(loaded, totalLen);
				await invoke('dl_end', { id, reveal: opts?.reveal !== false && getDownloadReveal(), abort: false });
				emitDlProgress({ key, name, loaded, total: totalLen || loaded, done: true });
			} catch (e) {
				// 취소·실패 공통 — 쓰다 만 파일 삭제(dl_end abort)
				await invoke('dl_end', { id, reveal: false, abort: true }).catch(() => undefined);
				const c = e instanceof DownloadCanceled;
				emitDlProgress({ key, name, loaded: 0, total: totalLen, done: true, error: !c, canceled: c });
				throw e;
			} finally { dlCancels.delete(key); }
			return;
		}
	}
	const blob = await readWithProgress(r, filename || 'download', resumeFrom);
	// 🌐 웹 — 사용자가 고른 위치에 직접 기록 (File System Access API). 미지원이면 아래 <a download>.
	if (saveTarget?.kind === 'web') {
		const w = await saveTarget.handle.createWritable();
		await w.write(blob);
		await w.close();
		return;
	}
	// 데스크톱(Tauri) — Downloads 폴더에 직접 저장 후 탐색기에서 해당 파일 선택 표시 (다운로드 완료 → 폴더 자동 오픈).
	// 파일명은 IPC 헤더 ASCII 제약 때문에 encodeURIComponent. 일괄 다운로드는 reveal=false 로 마지막 1회만 연다.
	if (isTauri()) {
		// Tauri 웹뷰는 <a download> 를 무시하므로 Rust save_download(디스크 저장+탐색기 표시)가 유일 경로.
		//   실패 시 조용히 삼키지 않고 오류를 그대로 전파 → 호출부(startDownload)가 사용자에게 사유 표시.
		const { invoke } = await import('@tauri-apps/api/core');
		const buf = await blob.arrayBuffer(); // ArrayBuffer 직접 전달(raw IPC 바디)
		// 📁 사용자가 고른 경로가 있으면 그 경로로 (2026-09-29) — 종전엔 이 경로(본문 스트림 없는 응답)에서 고른 경로를 버리고 지정 폴더에 저장했다
		if (saveTarget?.kind === 'tauri') { await writeBufferAt(saveTarget.path, buf, opts?.reveal !== false && getDownloadReveal()); return; }
		const dir = opts?.dir || getDownloadDir();
		await invoke('save_download', buf, {
			headers: {
				'x-filename': encodeURIComponent(filename || 'download'),
				'x-reveal': opts?.reveal === false || !getDownloadReveal() ? '0' : '1',
				// 📁 지정 다운로드 폴더 (2026-08-31 설정) — IPC 헤더 ASCII 제약이라 퍼센트 인코딩
				...(dir ? { 'x-dir': encodeURIComponent(dir) } : {}),
			},
		});
		return;
	}
	const objUrl = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = objUrl; a.download = filename || 'download';
	document.body.appendChild(a); a.click(); a.remove();
	setTimeout(() => URL.revokeObjectURL(objUrl), 5000);
}

/** 📁 데스크톱 — 사용자가 고른 경로에 버퍼를 그대로 기록 (2026-09-29). 스트리밍 저장과 같은 Rust 명령(dl_begin_at·dl_chunk·dl_end)을 쓴다 —
 *  save_download 는 파일명·폴더만 받아 '고른 경로 그대로' 저장이 안 되기 때문. 8MB 단위로 나눠 보내 IPC 한 번이 커지지 않게 한다. */
async function writeBufferAt(path: string, buf: ArrayBuffer, reveal: boolean): Promise<void> {
	const { invoke } = await import('@tauri-apps/api/core');
	const id = await invoke<number>('dl_begin_at', { path });
	try {
		const CH = 8 * 1024 * 1024;
		for (let off = 0; off < buf.byteLength; off += CH) {
			await invoke('dl_chunk', buf.slice(off, Math.min(buf.byteLength, off + CH)), { headers: { 'x-dl-id': String(id) } });
		}
		await invoke('dl_end', { id, reveal, abort: false });
	} catch (e) {
		await invoke('dl_end', { id, reveal: false, abort: true }).catch(() => undefined);
		throw e;
	}
}

// 💾 클라이언트가 생성한 blob 을 로컬 파일로 저장 — 엑셀/CSV 내보내기 등.
//   웹은 <a download>, 데스크톱(Tauri)은 웹뷰가 <a download> 를 무시하므로 Rust save_download
//   (다운로드 폴더 저장 + 탐색기 표시)가 유일 경로 (2026-07-14 — 관리자 엑셀이 데탑에서 무반응이던 원인).
export async function saveGeneratedFile(blob: Blob, filename: string): Promise<void> {
	if (isTauri()) {
		const { invoke } = await import('@tauri-apps/api/core');
		const buf = await blob.arrayBuffer();
		// 📁 '저장 위치 매번 묻기' 설정을 여기서도 따른다 (2026-09-29 "경로를 지정했는데 설정된 폴더로 들어감") — 종전엔 PDF 뷰어 저장·
		//   압축 미리보기 개별 저장·캡처 마킹·간트·엑셀 내보내기가 묻지 않고 지정 폴더로 바로 저장해, 다른 다운로드와 동작이 달랐다.
		//   취소는 실패가 아니다 — 조용히 끝낸다(호출부가 오류 창을 띄우지 않게).
		let target: SaveTarget = null;
		try { target = await pickSaveTarget(filename || 'download'); } catch (e) { if (e instanceof DownloadCanceled) return; throw e; }
		if (target?.kind === 'tauri') { await writeBufferAt(target.path, buf, getDownloadReveal()); return; }
		await invoke('save_download', buf, {
			headers: {
				'x-filename': encodeURIComponent(filename || 'download'),
				'x-reveal': getDownloadReveal() ? '1' : '0',
				// 📁 지정 다운로드 폴더 (2026-08-31 설정) — 엑셀 내보내기 등 생성물도 같은 폴더로
				...(getDownloadDir() ? { 'x-dir': encodeURIComponent(getDownloadDir()) } : {}),
			},
		});
		return;
	}
	// 📱 앱(안드·iOS 셸) — 다운로드 브릿지는 사내 서버 주소만 받는다(blob:/data: 거부 → '이 주소는 저장할 수 없습니다', 2026-10-08 설문 CSV).
	//   만든 파일을 사이드카에 잠깐 맡기고(/api/tpt/genfile, 10분·1회용) 그 주소를 브릿지에 넘긴다. 실패하면 아래 웹 방식으로.
	const appDl = typeof window !== 'undefined' ? window.TPTAndroid?.download : undefined;
	if (typeof appDl === 'function') {
		try {
			const r = await fetch(`/api/tpt/genfile?name=${encodeURIComponent(filename || 'download')}`, { method: 'POST', headers: { 'content-type': blob.type || 'application/octet-stream' }, body: blob });
			const j = await r.json().catch(() => ({})) as { url?: string };
			if (r.ok && j.url) { appDl.call(window.TPTAndroid, `${window.location.origin}${j.url}`, filename || 'download'); return; }
			diagLog('genfile upload fail', r.status);
		} catch (e) { diagLog('genfile upload error', String((e as Error).message || e)); }
	}
	const objUrl = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = objUrl; a.download = filename || 'download';
	document.body.appendChild(a); a.click(); a.remove();
	setTimeout(() => URL.revokeObjectURL(objUrl), 5000);
}

// 📤 외부 내보내기(공유) — OS 공유 시트(Web Share API)로 카톡 등 외부 앱에 파일 전달.
//   지원(주로 모바일 웹) 시 navigator.share({files}); 미지원(데스크탑/Tauri 등)이면 다운로드로 폴백.
//   반환: 'shared'(공유 시트 열림) | 'downloaded'(폴백) | 'canceled'(사용자 취소).
export async function shareFileExternal(url: string, filename: string, mime?: string): Promise<'shared' | 'downloaded' | 'canceled'> {
	// 🔐 외부로 내보내는 것도 '저장'이다 — downloadViaBlob 과 같은 규칙으로 dl=1 을 강제한다 (2026-08-17).
	//   이게 없으면 DRM 문서를 카톡 등으로 공유할 때 서버가 미리보기 경로로 판단해 **복호화 평문**을
	//   내보낸다(정책 위반). 호출부가 빠뜨려도 여기서 막히도록 함수 진입부에 둔다.
	if (url.includes('/api/tpt/file') && !/[?&]dl=1(?:&|$)/.test(url)) url += (url.includes('?') ? '&' : '?') + 'dl=1';
	// 📱 Android 앱(code 11+) — 웹뷰엔 Web Share API 가 없어 조용히 다운로드 폴백되던 것(2026-07-09).
	//   네이티브 브릿지가 파일을 받아 ACTION_SEND 공유 시트(카톡 등)를 연다. 구 APK 는 기존 폴백 유지.
	const androidShare = typeof window !== 'undefined' ? window.TPTAndroid?.shareFile : undefined;
	if (typeof androidShare === 'function') {
		// 🔴 네이티브 HttpURLConnection 도 헤더를 못 실음 — 직전 새 단기핸들로 교체 (2026-08-15, 다운로드와 동일)
		const abs = await withFreshFileHandle(url.startsWith('/') ? `${window.location.origin}${url}` : url);
		try { androidShare.call(window.TPTAndroid, abs, filename || 'file', mime || ''); return 'shared'; } catch { /* 폴백 진행 */ }
	}
	// blob 확보 — 프록시면 헤더 인증.
	let headers: Record<string, string> | undefined;
	if (url.includes('/api/tpt/file')) {
		const { authToken, userId } = useAuthStore.getState();
		if (authToken && userId) headers = { 'X-Auth-Token': authToken, 'X-User-Id': userId };
	}
	// 상대 경로(/api/...)는 데스크탑(tauri.localhost)에서 raw fetch 시 "Failed to fetch" → RC_ORIGIN 프리픽스(웹은 '' 라 무영향).
	const absUrl = url.startsWith('/') ? `${RC_ORIGIN}${url}` : url;
	let r: Response;
	try {
		r = await fetch(absUrl, headers ? { headers } : undefined);
	} catch (e) {
		// 데탑 등에서 커스텀 헤더 fetch 가 "Failed to fetch" 로 죽는 케이스 폴백 (2026-07-13) —
		// ① 프록시 URL 에 단기 핸들(t=)이 있으면 헤더 없이도 인증되므로 무헤더 재시도.
		// ② 그래도 실패하면(데탑 웹뷰 fetch 자체 문제) 기본 브라우저로 열어 브라우저가 직접 받게 함.
		try {
			if (headers && /[?&]t=/.test(absUrl)) r = await fetch(absUrl);
			else throw e;
		} catch (e2) {
			if (isTauri()) {
				const dlUrl = absUrl.includes('/api/tpt/file') && !/[?&]dl=1/.test(absUrl) ? `${absUrl}&dl=1` : absUrl;
				const { invoke } = await import('@tauri-apps/api/core');
				await invoke('open_external', { url: dlUrl });
				return 'downloaded'; // 브라우저가 저장 — 공유 대신 다운로드로 처리
			}
			throw new Error(`${(e2 as Error).message} — ${absUrl.slice(0, 80)}`);
		}
	}
	if (!r.ok) throw new Error(i18next.t('errors.download.failed', { status: r.status }));
	const blob = await r.blob();
	const file = new File([blob], filename || 'file', { type: mime || blob.type || 'application/octet-stream' });
	// Web Share API (level 2, files) — 데스크톱(Tauri)·구형 브라우저는 미지원 → 다운로드 폴백.
	const nav = navigator as Navigator & { canShare?: (d?: unknown) => boolean; share?: (d: unknown) => Promise<void> };
	if (!isTauri() && typeof nav.share === 'function' && typeof nav.canShare === 'function' && nav.canShare({ files: [file] })) {
		try {
			await nav.share({ files: [file], title: filename });
			return 'shared';
		} catch (e) {
			// 사용자가 공유 시트를 닫으면 AbortError — 다운로드 폴백하지 않고 취소로 처리.
			if ((e as Error).name === 'AbortError') return 'canceled';
			// 그 외 오류는 다운로드 폴백.
		}
	}
	await downloadViaBlob(url, filename);
	return 'downloaded';
}

// 🔎 전체 대화방 메시지 검색 — sidecar /api/tpt/search-messages (사용자 구독 방 한정 + 최근 days 일).
//   fileName = 파일 업로드 메시지의 파일명(본문이 비어 있을 때 미리보기용, 2026-09-16 — 서버가 파일명·첨부 제목도 매칭)
export interface GlobalMsgHit { _id: string; rid: string; roomName: string; roomType: string; msg: string; ts: string; username: string; name: string; fileName?: string }
export async function rcSearchMessagesGlobal(token: string, uid: string, q: string, days = 30): Promise<GlobalMsgHit[]> {
	const url = `${RC_ORIGIN}/api/tpt/search-messages?q=${encodeURIComponent(q)}&days=${days}`;
	const r = await fetch(url, { headers: { 'X-Auth-Token': token, 'X-User-Id': uid } });
	if (!r.ok) return [];
	const j = await r.json().catch(() => ({ results: [] }));
	return (j.results || []) as GlobalMsgHit[];
}

// 📎 전역 파일 검색 — 사이드카 /api/tpt/files/browse(파일 탭 브라우저와 같은 라우트: 구독 방 한정, 파일명·방 이름·업로더명 매칭,
//   원문 메시지 msgId 조인). 통합검색 팝업의 파일 탭이 UI 만 있고 공급자가 없던 것을 이 호출로 채운다 (2026-09-16).
export interface GlobalFileHit { id: string; name: string; size: number; mime: string; uploadedAt: string; rid: string; roomName: string; roomType: string; userId: string; userName: string; msgId: string; msgText: string }
export async function rcSearchFilesGlobal(token: string, uid: string, q: string, opts: { fromTs?: number; limit?: number } = {}): Promise<GlobalFileHit[]> {
	const qs = new URLSearchParams({ q, limit: String(opts.limit ?? 30) });
	if (opts.fromTs) qs.set('fromTs', String(opts.fromTs));
	const r = await fetch(`${RC_ORIGIN}/api/tpt/files/browse?${qs}`, { headers: { 'X-Auth-Token': token, 'X-User-Id': uid } });
	if (!r.ok) return [];
	const j = await r.json().catch(() => ({ items: [] }));
	return (j.items || []) as GlobalFileHit[];
}

// 🔎 방 안 검색 — 같은 사이드카 라우트에 rid 를 지정(기간 제한 없음, 서버가 구독 여부 재확인).
//   방 내 검색이 '이미 불러온 메시지'에서만 되던 것을 과거 전체로 넓힌다 (2026-08-14).
export async function rcSearchMessagesInRoom(token: string, uid: string, rid: string, q: string, limit = 100): Promise<GlobalMsgHit[]> {
	const url = `${RC_ORIGIN}/api/tpt/search-messages?rid=${encodeURIComponent(rid)}&q=${encodeURIComponent(q)}&limit=${limit}`;
	const r = await fetch(url, { headers: { 'X-Auth-Token': token, 'X-User-Id': uid } });
	if (!r.ok) return [];
	const j = await r.json().catch(() => ({ results: [] }));
	return (j.results || []) as GlobalMsgHit[];
}

// 내 아바타 변경 — users.setAvatar (multipart). 성공 시 true.
export async function rcSetAvatar(token: string, userId: string, file: File): Promise<boolean> {
	const fd = new FormData();
	fd.append('image', file, file.name || 'avatar');
	const res = await fetch(`${API_BASE}/users.setAvatar`, {
		method: 'POST', headers: { 'X-Auth-Token': token, 'X-User-Id': userId },
		body: fd,
	});
	const d = await res.json().catch(() => ({}));
	if (!res.ok || d.success === false) throw new Error(d.error || d.errorType || `setAvatar ${res.status}`);
	return true;
}

// RC 접속 상태(presence) 변경 — users.setStatus { status, message }. status: online|away|busy|offline.
export async function rcSetPresence(token: string, userId: string, status: 'online' | 'away' | 'busy' | 'offline', message?: string): Promise<boolean> {
	const res = await fetch(`${API_BASE}/users.setStatus`, {
		method: 'POST', headers: authHeaders(token, userId),
		body: JSON.stringify(message !== undefined ? { status, message } : { status }),
	});
	const d = await res.json().catch(() => ({}));
	if (!res.ok || d.success === false) throw new Error(d.error || d.errorType || `setStatus ${res.status}`);
	return true;
}

// 🙈 웹훅 서비스 계정 숨김 (2026-08-06 사용자 — "웹훅 발신 admin(tptalk)이 방 멤버로 보임") —
//   커넥터 카드 게시용 admin(username 'talk', 표시명 tptalk)이 비공개 방에 self-invite 로 멤버가 되므로
//   멤버 노출 UI(우측 패널 멤버 목록·멘션 자동완성·읽음확인)에서 숨긴다. 실제 멤버십은 유지(숨김 처리만).
//   ⚠️ DM 멤버(1:1 상대 카드)는 필터 제외 — admin 과의 DM 자체는 정상 표시.
export const SERVICE_ACCOUNT_USERNAMES = new Set(['talk']);
export function isServiceAccount(username?: string | null): boolean {
	return !!username && SERVICE_ACCOUNT_USERNAMES.has(username);
}

// M27.T2 — 방 멤버 (멘션 자동완성용). 채널/그룹/DM 분기.
// ⚡ 방 멤버 10s 캐시 + in-flight 병합(2026-07-02) — 방 전환 시 MessageView(멤버 표시)와 RichComposer(멘션)가
//   같은 방 members 를 동시에 불러 groups.members 가 2번(대형 방은 페이지네이션 ×2) 나가던 중복 제거.
//   TTL 짧아(10s) 초대/강퇴 반영 지연은 체감 없음.
const _membersCache = new Map<string, { at: number; data: Array<{ _id: string; username: string; name?: string }> }>();
const _membersInflight = new Map<string, Promise<Array<{ _id: string; username: string; name?: string }>>>();
// 멤버 변동(초대/강퇴) 직후 호출 — 캐시 무효화로 loadMembers 재조회가 stale 을 받지 않게 한다.
export function bustRoomMembersCache(roomId: string): void {
	for (const k of [..._membersCache.keys()]) if (k.startsWith(roomId + '|')) _membersCache.delete(k);
}
// 👥 멤버 변동 알림 (2026-08-19) — 캐시 무효화 + `tpt:members-changed` 발사.
//   종전에는 변동을 일으킨 화면이 자기 목록만 새로 불렀다. 그래서 방 설정 모달에서 내보내면
//   우측 패널 멤버 목록은 그대로였고(그 목록은 패널 open·방 전환 때만 로드), 반대도 마찬가지였다.
//   변동 지점은 이 함수 하나만 부르고, 목록을 그리는 쪽이 이벤트를 받아 갱신한다.
export function notifyMembersChanged(roomId: string): void {
	if (!roomId) return;
	bustRoomMembersCache(roomId);
	try { window.dispatchEvent(new CustomEvent('tpt:members-changed', { detail: { roomId } })); } catch { /* SSR/비브라우저 */ }
}
export async function rcRoomMembers(
	token: string,
	userId: string,
	roomId: string,
	roomType: 'c' | 'p' | 'd' | 'l',
): Promise<Array<{ _id: string; username: string; name?: string; active?: boolean }>> {
	const cacheKey = `${roomId}|${roomType}`;
	const hit = _membersCache.get(cacheKey);
	if (hit && Date.now() - hit.at < 10_000) return hit.data;
	const inflight = _membersInflight.get(cacheKey);
	if (inflight) return inflight;
	const p = _rcRoomMembersFetch(token, userId, roomId, roomType)
		.then((data) => { _membersCache.set(cacheKey, { at: Date.now(), data }); if (_membersCache.size > 100) { const k = _membersCache.keys().next().value; if (k !== undefined) _membersCache.delete(k); } return data; })
		.finally(() => { _membersInflight.delete(cacheKey); });
	_membersInflight.set(cacheKey, p);
	return p;
}
async function _rcRoomMembersFetch(
	token: string,
	userId: string,
	roomId: string,
	roomType: 'c' | 'p' | 'd' | 'l',
): Promise<Array<{ _id: string; username: string; name?: string; active?: boolean }>> {
	if (roomType === 'd') {
		// DM — im.members(이름 포함, RC 6.5 api/server/v1/im.ts 'im.members'). 🔴 종전엔 im.list 의 usernames 만 써서 name 이
		//   비었고 이름은 whois 로만 채워졌다 → whois 가 막히는 준회원(guest)·GW 미등록자에겐 우측 멤버가 전부 ID 로 보였다
		//   (2026-08-27 사용자 "이 직원으로 로그인하면 우측에 멤버가 id 가 뜸"). 실패하면 종전 im.list 폴백.
		try {
			const mu = new URL(`${API_BASE}/im.members`, window.location.origin);
			mu.searchParams.set('roomId', roomId);
			mu.searchParams.set('count', '100');
			const mr = await fetch(mu.pathname + mu.search, { headers: authHeaders(token, userId) });
			if (mr.ok) {
				const md = await mr.json();
				const members = (md.members ?? []) as Array<{ _id: string; username: string; name?: string }>;
				if (members.length) return members.map((m) => ({ _id: m._id, username: m.username, name: m.name, active: true }));
			}
		} catch { /* 폴백 */ }
		// 폴백 — im.list 에서 usernames 추출(이름 없음)
		const url = new URL(`${API_BASE}/im.list`, window.location.origin);
		url.searchParams.set('count', '0');
		const res = await fetch(url.pathname + url.search, { headers: authHeaders(token, userId) });
		if (!res.ok) return [];
		const data = await res.json();
		const room = (data.ims || []).find((r: { _id: string }) => r._id === roomId);
		if (!room) return [];
		const usernames: string[] = room.usernames || [];
		return usernames.map((u) => ({ _id: u, username: u }));
	}
	const ep = roomType === 'c' ? 'channels.members' : 'groups.members';
	// 전체 멤버 수집 — RC API_Upper_Count_Limit(기본 100)이 count 를 잘라, 멘션 자동완성/후임자 선정이
	//   100명 초과 방에서 누락됐다. offset 페이지네이션으로 전부 가져온다(상한 5000).
	//   active 도 함께 수집 — 퇴사/삭제(비활성) 멤버 내보내기 노출 판정용 (2026-07-15).
	const PAGE = 100;
	const out: Array<{ _id: string; username: string; name?: string; active?: boolean }> = [];
	for (let offset = 0; offset < 5000; offset += PAGE) {
		const url = new URL(`${API_BASE}/${ep}`, window.location.origin);
		url.searchParams.set('roomId', roomId);
		url.searchParams.set('count', String(PAGE));
		url.searchParams.set('offset', String(offset));
		const res = await fetch(url.pathname + url.search, { headers: authHeaders(token, userId) });
		if (!res.ok) break;
		const data = await res.json();
		const batch = (data.members ?? []) as Array<{ _id: string; username: string; name?: string; active?: boolean }>;
		out.push(...batch);
		const total = typeof data.total === 'number' ? data.total : undefined;
		if (batch.length < PAGE) break;
		if (total !== undefined && out.length >= total) break;
	}
	// 서비스 계정(웹훅 admin) 숨김 — 멘션 자동완성·방장 승계 후보 등 공용 소비처 일괄 제외 (c/p 전용, DM 은 위에서 반환됨)
	return out.filter((m) => !isServiceAccount(m.username));
}

// ⌨️ 입력 중 표시가 보내는 이름 키 (2026-09-04) — RC 스트리머 allowWrite 는 `UI_Use_Real_Name` 이 켜져 있으면 name, 아니면 username 과
//   글자 단위로 같아야 통과시킨다(notifications.module canType). 운영은 false(=username) 실측. 10분 캐시, 조회 실패 시 username.
let _useRealName: { ts: number; v: boolean } | null = null;
export async function rcUiUseRealName(token: string, userId: string): Promise<boolean> {
	if (_useRealName && Date.now() - _useRealName.ts < 10 * 60_000) return _useRealName.v;
	try {
		const q = encodeURIComponent(JSON.stringify({ _id: { $in: ['UI_Use_Real_Name'] } }));
		const r = await fetch(`${API_BASE}/settings.public?query=${q}&count=5`, { headers: authHeaders(token, userId) });
		if (!r.ok) return false;
		const j = await r.json();
		const row = ((j.settings || []) as Array<{ _id: string; value: unknown }>).find((s) => s._id === 'UI_Use_Real_Name');
		_useRealName = { ts: Date.now(), v: row?.value === true };
		return _useRealName.v;
	} catch { return false; }
}

// 🔐 로그인 실패 보고 (2026-09-14 사용자 "LOGIN 실패 5회 시 비번찾기로 다시 하라고 해야 함")
//   로그인은 RC 직행이라 사이드카가 실패를 모른다 → 화면이 직접 알려 준다. 서버는 기록만 하고
//   {count, suggestReset} 만 돌려준다(🔒 계정 존재 여부는 응답에 없다).
//   실패해도 조용히 넘긴다 — 이 보고 때문에 로그인 화면이 막히면 안 된다.
export async function reportLoginFail(user: string, reason: string): Promise<{ count: number; suggestReset: boolean }> {
	try {
		const r = await fetch('/api/tpt/auth/login-fail', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ user, reason }),
		});
		const j = await r.json().catch(() => ({})) as { count?: number; suggestReset?: boolean };
		return { count: Number(j.count) || 0, suggestReset: !!j.suggestReset };
	} catch { return { count: 0, suggestReset: false }; }
}
